import { ApiError } from './api-error';
import { followPublication, type PublicationJob } from './publication';
/**
 * Typed API client. Attaches the Cognito ID token as Bearer on every call,
 * surfaces 401s so the caller can route to login, and parses JSON errors
 * into a uniform ApiError.
 *
 * Types mirror backend/src/schemas/entities.ts — kept in sync by hand
 * until we publish the backend Zod schemas as a shared package.
 */

import { ENV } from '../env';
import type { WolfClassification } from '../../../backend/src/lib/review-readiness';
import { clearTokens,ensureFreshTokens,refreshTokens,type AuthRefreshError } from './auth';

export { ApiError } from './api-error';

async function doFetch(path: string, init: RequestInit, idToken: string): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('authorization', `Bearer ${idToken}`);
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  return fetch(`${ENV.apiUrl}${path}`, { ...init, headers });
}

// A refresh that could not reach Cognito (or got a server/garbled reply) keeps
// the session and stops the request: sending it unauthenticated would 401 and
// sign the user out over a blip. Matched by name so auth module stand-ins
// without the class still link.
function isAuthRefreshError(cause: unknown): cause is AuthRefreshError {
  return cause instanceof Error && cause.name === 'AuthRefreshError';
}

async function withRefreshFailureAsApiError<R>(run: () => Promise<R>): Promise<R> {
  try {
    return await run();
  } catch (cause) {
    if (isAuthRefreshError(cause)) {
      throw new ApiError(
        503,
        'auth_refresh_unavailable',
        'Could not refresh your sign-in. Check your connection and try again.',
        { retryable: true, reason: cause.reason, status: cause.status ?? null },
      );
    }
    throw cause;
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Pre-flight: refresh proactively if the token is within the skew window.
  // ensureFreshTokens is single-flight, so concurrent calls share one refresh.
  const tokens = await withRefreshFailureAsApiError(ensureFreshTokens);
  let res = tokens
    ? await doFetch(path, init, tokens.idToken)
    : await fetch(`${ENV.apiUrl}${path}`, init);

  // Reactive: if the API rejects despite our pre-flight check (clock skew,
  // server-side revocation, race), try one refresh + retry before giving up.
  if (res.status === 401 && tokens) {
    const refreshed = await withRefreshFailureAsApiError(refreshTokens);
    if (refreshed) {
      res = await doFetch(path, init, refreshed.idToken);
    }
  }

  if (res.status === 401) {
    clearTokens();
    throw new ApiError(401, 'not_authenticated');
  }

  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      if (res.ok) {
        throw new ApiError(
          502,
          'invalid_api_response',
          'The API returned a non-JSON success response.',
          { contentType: res.headers.get('content-type') },
        );
      }
      body = { message: text.slice(0, 300) };
    }
  }

  if (res.ok && text) {
    const contentType = res.headers.get('content-type')?.toLowerCase() ?? '';
    if (!contentType.includes('application/json') && !contentType.includes('+json')) {
      throw new ApiError(
        502,
        'invalid_api_response',
        'The API returned JSON with an invalid content type.',
        { contentType },
      );
    }
  }

  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; message?: string };
    const message = b.error === 'unsupported_mime'
      ? 'This file format is not supported. Choose PDF, PNG, JPG, WEBP, GIF, CSV, TSV, XLSX, PPTX, TXT, MD, HTML, or JSON.'
      : b.message ?? res.statusText;
    throw new ApiError(res.status, b.error ?? 'http_error', message, body);
  }
  return body as T;
}

// A queued request may wait this long for a live native worker (backend APPLICATION_QUEUE_HOLD_SECONDS);
// once a worker claims it, its deadline shrinks to the run window.
export const APPLICATION_QUEUE_HOLD_MS = 86_400_000;
const APPLICATION_CLOCK_SKEW_MS = 300_000;
const OPERATION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
type ApplicationTicket = { application_operation: string; expires_at: string; paused?: boolean };
type ApplicationState = { status: string; response: { statusCode: number; body: unknown } | null;
  expires_at?: string; paused?: boolean };

export const APPLICATION_PAUSED_MESSAGE =
  'Processing is paused; request queued. Starmap will run it automatically when processing resumes.';

export interface ApplicationPausedDetails {
  application_operation: string;
  client_id: string | null;
  expires_at: string;
}

/** The request is saved and queued, but no native worker is live to run it right now. */
export function applicationPausedDetails(cause: unknown): ApplicationPausedDetails | null {
  return cause instanceof ApiError && cause.code === 'application_operation_paused'
    ? cause.body as ApplicationPausedDetails : null;
}

function applicationDeadline(expiresAt: string | undefined): number {
  const deadline = Date.parse(expiresAt ?? '');
  if (!Number.isFinite(deadline) || deadline > Date.now() + APPLICATION_QUEUE_HOLD_MS + APPLICATION_CLOCK_SKEW_MS) {
    throw new ApiError(502, 'invalid_application_operation');
  }
  return deadline;
}

function pausedError(operationId: string, clientId: string | null, expiresAt: string): ApiError {
  return new ApiError(202, 'application_operation_paused', APPLICATION_PAUSED_MESSAGE,
    { application_operation: operationId, client_id: clientId, expires_at: expiresAt } satisfies ApplicationPausedDetails);
}

async function awaitApplicationOperation<T>(operationId: string, firstExpiresAt: string, partnerId: string,
  signal: AbortSignal | undefined, clientId: string | null): Promise<T> {
  if (!OPERATION_ID.test(operationId)) throw new ApiError(502, 'invalid_application_operation');
  let deadline = applicationDeadline(firstExpiresAt);
  const operationPath = withPartner(`/v1/application-operations/${operationId}`, partnerId);
  while (true) {
    const state = await call<ApplicationState>(operationPath, signal ? { signal } : {});
    if (state.status === 'completed' || state.status === 'failed') {
      const response = state.response;
      if (!response || !Number.isInteger(response.statusCode) || response.statusCode < 100 || response.statusCode > 599) {
        throw new ApiError(502, 'invalid_application_response');
      }
      if (response.statusCode >= 400) {
        const body = response.body as { error?: string; message?: string } | null;
        throw new ApiError(response.statusCode, body?.error ?? 'application_operation_failed', body?.message, response.body);
      }
      return response.body as T;
    }
    if (!['queued','running'].includes(state.status)) throw new ApiError(503, 'application_operation_interrupted',
      'This request was interrupted. Check its saved state before trying again.', state);
    if (state.expires_at !== undefined) deadline = applicationDeadline(state.expires_at);
    if (state.status === 'queued' && state.paused === true) {
      throw pausedError(operationId, clientId, new Date(deadline).toISOString());
    }
    if (Date.now() >= deadline + 30_000) throw new ApiError(504, 'application_operation_timeout');
    await new Promise<void>((resolve, reject) => {
      const abort = () => { clearTimeout(timer); signal?.removeEventListener('abort', abort); reject(signal?.reason); };
      const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, 1000);
      signal?.addEventListener('abort', abort, { once: true });
      if (signal?.aborted) abort();
    });
  }
}

async function callApplication<T>(path: string, partnerId: string, init: RequestInit = {}): Promise<T> {
  if (init.signal?.aborted) throw init.signal.reason;
  const clientId = crypto.randomUUID();
  const headers = new Headers(init.headers);
  headers.set('x-starmap-operation-id', clientId);
  const requestPath = `/v1/application-operation-requests/${clientId}`;
  let cancellation: Promise<unknown> | undefined;
  const cancel = () => {
    cancellation ??= call(withPartner(`${requestPath}/cancel`, partnerId), { method: 'POST' }).catch(() => {});
  };
  init.signal?.addEventListener('abort', cancel, { once: true });
  try {
    let first: T | ApplicationTicket;
    try {
      first = await call(path, { ...init, headers });
    } catch (cause) {
      if (init.signal?.aborted) { cancel(); throw init.signal.reason; }
      if (cause instanceof ApiError && (cause.status < 500
          || !['http_error','internal_error','invalid_api_response'].includes(cause.code))) throw cause;
      first = await call<T | ApplicationTicket>(withPartner(`${requestPath}/recover`, partnerId), {
        method: 'POST', ...(init.signal ? { signal: init.signal } : {}),
      }).catch(() => {
        if (init.signal?.aborted) { cancel(); throw init.signal.reason; }
        throw new ApiError(503, 'application_request_recovery_unavailable',
          'The request response was lost. Check its saved state before trying again.', { client_id: clientId });
      });
    }
    if (init.signal?.aborted) { cancel(); throw init.signal.reason; }
    if (!first || typeof first !== 'object' || !('application_operation' in first)) return first as T;
    if (!OPERATION_ID.test(first.application_operation)) throw new ApiError(502, 'invalid_application_operation');
    const deadline = applicationDeadline(first.expires_at);
    // Paused: return at once with the saved operation ID instead of waiting out the queue hold.
    if (first.paused === true && deadline > Date.now()) {
      throw pausedError(first.application_operation, clientId, first.expires_at);
    }
    return await awaitApplicationOperation<T>(first.application_operation, first.expires_at, partnerId, init.signal ?? undefined, clientId);
  } finally { init.signal?.removeEventListener('abort', cancel); }
}

// ── Types ────────────────────────────────────────────────────────────────
export type EngagementStatus = 'green' | 'yellow' | 'red';
export interface EngagementRatingSuggestion {
  id: string;
  seller_name: string;
  target_name: string;
  target_pid: string;
  note: string;
  source_quote: string;
  suggested_status: EngagementStatus;
  contradiction: boolean;
}
export type EngagementWriteMode = 'canonical' | 'canonical_override';
export type WorkspaceStatus = 'active' | 'pilot' | 'archived';

export interface PartnerWorkspace {
  partner_id: string;
  name: string;
  slug: string;
  status: WorkspaceStatus;
  logo_key: string | null;
  domain_hints: string[];
  email_alias_patterns: string[];
  default_tabs: unknown[];
  feature_flags: Record<string, unknown>;
  ingestion_rules_ref: string | null;
}

export interface Person {
  partner_id: string;
  pid: string;
  name: string;
  email: string | null;
  email_alias: string | null;
  role: string | null;
  title?: string | null;
  level: string | null;
  departed_on?: string | null;
  location: string | null;
  tenure: string | null;
  reports_count: number | null;
  coverage_org: string | null;
  aws_territories?: {code:string;display_name:string}[];
  caylent_segment: string | null;
  manager_pid: string | null;
  notes: string | null;
  is_active: boolean;
  source_tab: string | null;
  /** First-class inferred/provisional flag (prototype .inferred). */
  provisional?: boolean;
  /** Vacated SEAT (0048): the position persists after its holder departed — renders as
   *  "TBH (FKA <name>, left <date>)" (or verbatim for '???' positional names) on every surface that names a person.
   *  A seat is an ORDINARY ACTIVE NODE (owner ruling 2026-07-25, enforced by 0050's
   *  person_vacancy_active): vacancy=true implies is_active=true, and a seat is selectable,
   *  searchable and chartable exactly like any other person. The label drives display only. */
  vacancy?: boolean;
  /** TBH seat lifecycle (0092). A "Mark left AWS" correction turns the person into an active
   *  'tbh' seat that keeps its manager, team, title, accounts and counterpart roles. */
  seat_state?: SeatState | null;
  seat_former_name?: string | null;
  seat_vacated_on?: string | null;
  /** 0093: 'month' when only the departure month is known (seat_vacated_on is then its last day). */
  seat_vacated_precision?: SeatDatePrecision | null;
  seat_req_url?: string | null;
  seat_req_opened_on?: string | null;
  seat_filled_by_pid?: string | null;
  /** Backend-computed name ("TBH (FKA <name>, left <date>[, open req])" for a seat). */
  display_name?: string;
  /** Superadmin confirmation (0093) that this unparented person is the top of their org. */
  org_root_confirmed_at?: string | null;
  org_root_confirmed_by?: string | null;
}

export type SeatState = 'tbh' | 'open_rec' | 'filled' | 'eliminated';
export type SeatDatePrecision = 'day' | 'month';
export type DepartureBody = { date: string; precision?: SeatDatePrecision };
export type PersonCorrectionKind = 'profile' | 'departure' | 'seat_state' | 'seat_fill' | 'seat_eliminate' | 'org_root' | 'succession' | 'account_link_end';
export type SeatActionBody =
  | { action: 'tbh' }
  | { action: 'open_rec'; req_url?: string | null; req_opened_on?: string | null }
  | { action: 'fill'; date: string; pid: string }
  | { action: 'fill'; date: string; name: string; alias?: string; email?: string }
  | { action: 'fill'; date: string; proposal_id: string }
  | { action: 'eliminate'; date: string }
  | { action: 'reject_fill'; proposal_id: string };
export type SeatStateResult = {
  correction_id: string | null; seat_pid: string; seat_state: SeatState;
  seat_req_url?: string | null; seat_req_opened_on?: string | null;
};
export type SeatFillResult = {
  correction_id: string; seat_pid: string; successor_pid: string; successor_created: boolean;
  proposal_id?: string | null;
};
export type SeatRejectFillResult = { proposal_id: string; status: 'rejected' };
export type SeatEliminateResult = { correction_id: string; seat_pid: string; seat_state: 'eliminated' };
export type DepartureResult = {
  correction_id: string; pid: string; departed_on: string; departed_precision: SeatDatePrecision; vacancy: true;
  seat_state: 'tbh'; display_name: string;
};
/** An ingestion candidate that looks like the person filling a TBH seat (0092). The merge holds it
 *  for an owner decision only when exactly one seat matched; it never creates the person itself. */
export interface SeatFillProposal {
  id: string; ingestion_id: string; candidate_id: string;
  proposed: {
    name?: string | null; email?: string | null; email_alias?: string | null;
    role?: string | null; level?: string | null; location?: string | null;
  };
  match_basis: { basis?: 'manager' | 'former_holder_named' | string; former_name?: string | null };
  status: 'proposed'; created_at: string;
}

/** Stamped by a historical import on each reference value it wrote; values seeded as current carry none. */
export interface ReferenceProvenance {
  source?: string;
  confidence?: string;
  observed_at?: string;
  evidence_ref?: string;
}
export interface OverlayEntry {
  overlay: string;
  mgr: string;
  provenance?: ReferenceProvenance;
}
export interface NameAliasEntry {
  name: string;
  alias: string;
  provenance?: ReferenceProvenance;
}
export interface ReferenceData {
  overlay?: Record<string, OverlayEntry>;     // keyed by caylent_segment
  ps_ae?: Record<string, NameAliasEntry>;     // keyed by DM data-id
  cy_manager?: Record<string, string>;        // cy-pid → 'tari' | 'boyd'
  ppl?: Record<string, NameAliasEntry>;       // keyed by caylent_segment
  msa?: Record<string, boolean>;              // account name -> true
  sf_url?: Record<string, string>;            // account name -> Salesforce URL
  active?: Record<string, boolean>;           // account name -> active project or pipeline
  seg_labels?: Record<string, string[]>;      // segment key -> display labels
  seg_leaders?: Record<string, Array<{ name: string; id: string; role?: string; tab?: string }>>; // keyed by segment label
}

export interface Account {
  parent_account_id?: string | null;
  partner_id: string;
  account_id: string;
  caylent_lead_pid?: string | null;
  name: string;
  segment: string | null;
  arr_usd: number | null;
  proserve_ae_pid: string | null;
  note: string | null;
  hold: boolean;
  note_updated_at: string | null;
  hold_updated_at: string | null;
}

export interface PersonCorrectionHistory {
  person: Pick<Person, 'pid' | 'name' | 'role' | 'title' | 'level' | 'is_active' | 'departed_on' | 'source_tab'>
    & Partial<Pick<Person, 'manager_pid' | 'email_alias'>>
    & Required<Pick<Person, 'vacancy' | 'seat_state' | 'seat_former_name' | 'seat_vacated_on' | 'seat_req_url'
      | 'seat_req_opened_on' | 'seat_filled_by_pid'>> & Partial<Pick<Person, 'seat_vacated_precision'>>
    & { display_name: string };
  can_edit: boolean;
  seat_fill_proposals: SeatFillProposal[];
  /** Also-known-as aliases beyond the primary email alias, from uploads or this editor. */
  aliases?: Array<{ alias: string; source: 'upload' | 'manual' }>;
  corrections: Array<{
    id: string; pid: string; related_pid: string | null; kind: PersonCorrectionKind; effective_on: string | null;
    effective_precision?: SeatDatePrecision | null;
    can_undo?: boolean;
    actor_email: string; source: 'manual correction'; created_at: string;
    undone_at: string | null; undone_by: string | null; account_id: string | null;
    before_state: Record<string, unknown> | null; after_state: Record<string, unknown> | null;
    /** 'month' when the departure was recorded by month. */
    precision?: 'month' | null;
    /** A succession's statement and who made it; a statement from an upload or Slack also names its candidate. */
    source_statement?: string | null;
    source_stated_by?: string | null;
    source_url?: string | null;
    source_ingestion_id?: string | null;
    source_candidate_id?: string | null;
  }>;
}

/** "X is the new Y": X is chosen by exact person, or added as a new person; never matched by first name. */
export type SuccessionBody = {
  successor: { kind: 'existing'; pid: string } | { kind: 'new'; name: string; alias?: string; email?: string };
  /** The date of the statement; the change is recorded as of this day. */
  date: string;
  /** Only when the statement says the person being replaced left the company. A month is sent as its
   *  last day with precision 'month', so the statement date is never recorded as the day they left. */
  departure?: { date: string; precision: SeatDatePrecision };
  source: { ingestion_id: string; candidate_id: string } | { statement: string; stated_by: string; url?: string };
  /** The owner confirms the selected people are the ones an incomplete stated name (surname, nickname) means. */
  identity_confirmed?: true;
  preview?: boolean;
};
export type SuccessionStatedIdentity = { stated: string; pid: string | null; name: string; basis: string };
export type SuccessionResult = {
  correction_id: string | null; preview: boolean;
  predecessor_pid: string; successor_pid: string; seat_pid: string | null;
  successor_created: boolean; full_name_unconfirmed: boolean; summary: string[];
  predecessor_departure?: { departed_on: string; precision: SeatDatePrecision } | null;
  source: { statement: string; stated_by: string; url: string | null; ingestion_id: string | null; candidate_id: string | null };
  identity_confirmation: {
    required: boolean; confirmed: boolean; predecessor: SuccessionStatedIdentity; successor: SuccessionStatedIdentity;
  } | null;
};

export interface PersonProfileCorrection {
  role?: string | null;
  title?: string | null;
  level?: string | null;
  manager_pid?: string | null;
  aliases?: string[];
}

export interface Team {
  partner_id: string;
  team_id: string;
  name: string;
  org: string | null;
  manager_pid: string | null;
  kind?: 'group' | 'cost_center' | 'reporting';
  cost_center_code?: string | null;
}

export interface Relationship {
  partner_id: string;
  rel_id: string;
  src_pid: string;
  dst_pid: string;
  rel_type: string;
  confidence: string;
  notes: string | null;
  label: string | null;
  rel_subtype: string | null;
  since: string | null;
  src_asset: string | null;
  evidence_ref: string | null;
  /** Confidence after age decay (Principle #18) — computed server-side. */
  effective_confidence: string;
}

export interface EvidenceRow {
  partner_id: string;
  evidence_ref: string;
  type: string;
  source_name: string | null;
  observed_at: string | null;
  conf_baseline: string | null;
}

export interface EngagementCurrentRow {
  display_status?: EngagementStatus;
  unrated?: boolean;
  team_display_status?: EngagementStatus;
  team_unrated?: boolean;
  partner_id: string;
  scope: 'person' | 'account';
  key: string;
  status: EngagementStatus | null;
  team_status: EngagementStatus | null;
  rated_by: string | null;
  rated_at: string | null;
  team_rated_at: string | null;
  team_set_by_email?: string | null;
  team_on_behalf_of_email?: string | null;
  notes: string | null;
  set_by_email?: string | null;
  set_by_name?: string | null;
  set_by_role?: 'owner' | 'manager' | 'admin_override' | null;
  on_behalf_of_email?: string | null;
  set_at?: string | null;
}

export interface EngagementHistoryRow {
  partner_id: string;
  scope: 'person' | 'account';
  key: string;
  field: 'status' | 'team_status';
  status: EngagementStatus | null;
  set_by_email: string;
  set_by_role: 'owner' | 'manager' | 'admin_override';
  on_behalf_of_email: string | null;
  set_at: string;
  superseded_at: string | null;
  recorded_by_email?: string | null;
  source_kind?: 'owner statement' | 'uploaded source' | null;
}

export interface HeatmapImportInput {
  type: 'person' | 'account' | 'team';
  target: string;
  seller_email: string;
  seller_name?: string;
  status: EngagementStatus;
}
export interface HeatmapImportResponseRow {
  row: number;
  ok: boolean;
  error?: string;
}

export interface AccountRelationship {
  partner_id: string;
  rel_id: string;
  account_id: string;
  pid: string;
  rel_type: string;
  confidence: string;
  src_asset?: string | null;
  evidence_ref?: string | null;
}

export type XrefGroupRuleType =
  | 'team_to_caylent'
  | 'territory_to_caylent'
  | 'segment_to_caylent'
  | 'segment_to_ppl'
  | 'team_to_proserve';

export interface XrefGroupRule {
  rule_id: string;
  rule_type: XrefGroupRuleType;
  source_key: string;
  target_pid: string;
  confidence: 'confirmed' | 'high' | 'medium' | 'low' | 'inferred';
  priority: number;
  decision_source: 'manual' | 'ingestion' | 'prototype';
  source_ingestion_id: string | null;
  evidence: Record<string, unknown>;
  effective_from: string;
}

export interface EngagementPersonRow {
  partner_id: string;
  target_pid: string;
  rep_pid: string;
  status: EngagementStatus;
  notes: string | null;
  updated_by_user_id: string;
  updated_at: string;
}

export interface EngagementAccountRow {
  partner_id: string;
  account_id: string;
  rep_pid: string;
  status: EngagementStatus;
  notes: string | null;
  updated_by_user_id: string;
  updated_at: string;
}

export interface EngagementPersonalRow {
  partner_id: string;
  scope: 'person' | 'account';
  key: string;
  user_id: string;
  user_email: string;
  linked_pid: string | null;
  status: EngagementStatus;
  notes: string | null;
  updated_at: string;
  source: 'linked' | 'user';
}

export interface Aggregate<K extends string> {
  aggregate_status: EngagementStatus | null;
  rating_count: number;
  target_pid?: string;
  account_id?: string;
  [key: string]: unknown;
  _key?: K;
}

export interface MeResponse {
  email: string;
  userId: string;
  groups: string[];
  role: 'member' | 'admin' | 'superadmin';
  isAdmin: boolean;
  isSuperAdmin: boolean;
  person: {
    pid: string;
    name: string;
    email: string | null;
    manager_pid: string | null;
    caylent_segment: string | null;
    role: string | null;
  } | null;
}

/** Why a person still has no manager, or an account no lead, after the Wolf placement sweep. */
export interface PlacementReasonRow {
  entity_kind: 'person' | 'account';
  entity_id: string;
  entity_name?: string;
  status?: 'queued' | 'in_review' | 'verified' | 'question' | 'system_issue' | 'waiting' | 'undone';
  reason_code: string;
  reason: string;
  updated_at: string;
}

export interface BootstrapResponse {
  engagement_breakdown?: Record<'people' | 'accounts', {
    total: number; green: number; yellow: number; red: number; unrated: number;
    display_counts: Record<EngagementStatus, number>;
  }>;
  active_partner_id: string;
  workspace: PartnerWorkspace;
  workspaces: PartnerWorkspace[];
  people: Person[];
  accounts: Account[];
  teams: Team[];
  coverage_team?: Array<{ partner_id: string; team_id: string; manager_pid: string; caylent_team_id: string; caylent_lead_pid: string }>;
  /** Team memberships (pid ↔ team_id) — ships the Wolf-minted org teams to the client. */
  person_teams?: Array<{ partner_id: string; pid: string; team_id: string }>;
  relationships: Relationship[];
  account_relationships: AccountRelationship[];
  engagement_person: EngagementPersonRow[];
  engagement_account: EngagementAccountRow[];
  engagement_personal: EngagementPersonalRow[];
  engagement_current: EngagementCurrentRow[];
  evidence: EvidenceRow[];
  /** Accounts sitting in the pending duplicate-review queue. */
  dupe_account_ids: string[];
  /** Accounts with open opportunity pipeline or active project signals. */
  active_account_ids: string[];
  /** Accounts with canonical Salesforce external references. */
  sf_account_ids: string[];
  /** Accounts with any confirmed/proxy MSA evidence. */
  msa_account_ids: string[];
  confirmed_msa_account_ids: string[];
  proxy_msa_account_ids: string[];
  /** External data systems with at least one completed import in this workspace. */
  external_import_systems?: string[];
  /** Durable typed group XREF rules. Missing means this backend predates the role graph. */
  xref_group_rules?: XrefGroupRule[];
  /** Read-only citations from a canonical source org chart: a source root and one of its direct
   *  reports each matched one current person, and that report currently sits under that root. */
  org_root_source_evidence?: {
    source_file: string;
    extracted_from: string;
    as_of: string;
    links: Array<{ root_pid: string; report_pid: string; source_root_id: string; source_report_id: string }>;
  };
  /** Missing means this backend predates the Wolf placement sweep. */
  placement_reasons?: PlacementReasonRow[];
  engagement_person_aggregate: Array<{
    target_pid: string;
    aggregate_status: EngagementStatus | null;
    rating_count: number;
  }>;
  engagement_account_aggregate: Array<{
    account_id: string;
    aggregate_status: EngagementStatus | null;
    rating_count: number;
  }>;
  coverage_person: Array<{
    partner_id: string;
    target_pid: string;
    caylent_team_id: string;
    caylent_lead_pid: string;
    channel: 'seg' | 'team' | 'assigned' | null;
    derived: boolean;
  }>;
  coverage_account: Array<{
    partner_id: string;
    account_id: string;
    caylent_team_id: string;
    caylent_lead_pid: string;
    channel: 'seg' | 'team' | 'assigned' | null;
    derived: boolean;
  }>;
  /** Person, team and account Caylent reps explicitly removed and left unassigned. */
  coverage_removals?: CoverageRemoval[];
  reference_data: ReferenceData;
  /** D2: published facts that are still the best available call. Missing means this backend predates D2. */
  uncertain_facts?: UncertainFact[];
}

/** One fact an upload published as its best available call, marked uncertain until the owner reviews it. */
export interface UncertainFact {
  id: string;
  statement: string;
  reason: string;
  alternatives: string[];
  /** "(uncertain: reason; alternatives: …)" */
  marker: string;
  person_ids: string[];
  account_ids: string[];
  rel_ids: string[];
  source: { ingestion_id: string; candidate_id: string; file_name: string | null };
  created_at: string;
}

/** One call in the Review later list. */
export interface ReviewLaterCall {
  id: string;
  ingestion_id: string;
  candidate_id: string;
  category: string;
  outcome: 'published' | 'kept_out';
  statement: string;
  reason: string;
  alternatives: { label: string; candidate_id?: string }[];
  status: 'open' | 'confirmed' | 'switched' | 'undone';
  resolution: { action?: string; undone?: number; left?: unknown[] } | null;
  created_at: string;
  resolved_at: string | null;
  file_name: string | null;
}

export interface CoverageRemoval {
  id: string;
  scope: 'person' | 'account';
  key: string;
  prior_lead_pid: string | null;
  displayed_lead_pid: string | null;
  removed_by: string;
  removed_at: string;
}

export interface CoverageRemovalResponse {
  scope: 'person' | 'account';
  subject_key: string;
  removal_id: string;
  prior: { caylent_lead_pid: string; caylent_team_id: string } | null;
}

export interface EngagementWriteResponse {
  target_pid: string;
  visibility: 'canonical';
  write_mode: EngagementWriteMode;
  status: EngagementStatus | null;
  team_status: EngagementStatus | null;
  set_by_email: string;
  set_by_role: 'owner' | 'manager' | 'admin_override';
  on_behalf_of_email: string | null;
}

export interface EngagementAccountWriteResponse extends Omit<EngagementWriteResponse, 'target_pid'> {
  account_id: string;
}

export type IngestionStatus =
  | 'transcribed'
  | 'uploaded'
  | 'extracting'
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'failed';

/** An upload's Undo: when and by whom, and (when recorded) how many of its changes it undid and left as is. */
export interface UploadUndoState { at: string; by: string; undone?: number; total?: number; left?: number }

export interface UploadFailureRecord {
  step: 'upload' | 'native_call' | 'extraction' | 'version_judge' | 'content_dating' | 'verification' | 'wolf' | 'publication';
  error_class: string; message: string; attempt: number; at: string; retry_at?: string | null; processing?: boolean | null;
}

export interface IngestionListItem {
  review_classification_ready?: boolean;
  test_run_id?: string | null;
  id: string;
  partner_id: string;
  uploader_email: string;
  file_mime: string;
	  file_bytes: number;
	  status: IngestionStatus;
	  created_at: string;
	  extracting_started_at: string | null;
	  reviewed_at: string | null;
  workspace_warning: string | null;
  workspace_warning_acknowledged: boolean;
  batch_id: string | null;
  file_label: string | null;
  extraction_error: string | null;
  failure_records?: UploadFailureRecord[];
  notification_status: 'pending' | 'sent' | 'failed' | 'skipped' | null;
  notification_channel: 'slack' | null;
  notification_error: string | null;
  notification_available_at: string | null;
  candidate_count: string;
  pending_candidate_count: string;
  /** Missing on backends that predate these columns. */
  approved_candidate_count?: string;
  /** Pending and approved changes per kind, one entry per kind present. */
  candidate_type_counts?: Record<string, { pending: number; approved: number }> | null;
  auto_retry_attempts?: number | null;
  auto_retry_next_at?: string | null;
  auto_retry_exhausted?: boolean | null;
  /** Status of the batch's latest Wolf run, only where Wolf is enabled for the batch; null otherwise. */
  batch_wolf_status?: WolfBatchStatus['status'] | null;
  undo?: UploadUndoState | null;
}

export interface IngestionBatch {
  model_routes?: { ingestion: 'native' | 'bedrock'; wolf: 'native' | 'bedrock' } | null;
  id: string;
  partner_id: string;
  created_by: string | null;
  uploader_email: string | null;
  file_count: number;
  created_at: string;
}

export interface IngestionBatchMember {
  failure_records?: UploadFailureRecord[];
  id: string;
  status: IngestionStatus;
  file_mime: string;
  file_bytes: number;
  uploader_email: string;
  created_at: string;
  extracting_started_at: string | null;
  candidate_count: string;
  undo?: UploadUndoState | null;
}

export interface IngestionBatchDetail {
  batch: IngestionBatch;
  companion_context_pairs: Array<{ spreadsheet_id: string; note_id: string;
    spreadsheet_label: string; note_label: string }>;
  rating_suggestions: Array<{ id: string; ingestion_id: string; seller_name: string;
    target_name: string; note: string; suggested_status: 'green' | 'yellow' | 'red';
    source_line: number | null; source_quote: string; contradiction: boolean; status: 'pending' | 'confirmed' }>;
  /** Whose book each source is, and what became of each explicit color it shows. */
  seller_books?: SellerBookSummary[];
  ingestions: (IngestionBatchMember & {
    file_label: string;
    has_approved: boolean;
    fuse_group_id: string | null;
    content_integrity_violation: boolean;
  })[];
  /** UNCAPPED (unlike the reconcile) — the group card renders from this read so a giant batch
   *  can never hide the fusion recovery actions behind the reconcile's member cap. */
  fuse_groups: FuseGroup[];
  /** True when fusion state moved while this response was being computed — refetch to act. */
  fusion_changed_midread: boolean;
  /** Open vacated-seat decisions, on the UNCAPPED read for the same reason fuse_groups are: the
   *  reconcile refuses a >cap batch outright, so a proposal surfaced only there was invisible on
   *  exactly those batches — while approve still detects and refuses on it, wedging the records. */
  vacancy_proposals?: VacancyProposalSummary[];
}

/** One open vacated-seat decision. Identical shape from the reconcile and the plain read so a
 *  single panel renders both. */
export interface VacancyProposalSummary {
  id: string;
  seat_pid: string;
  seat_name: string;
  grandparent_pid: string;
  grandparent_name: string;
  affected: Array<{ candidate_id: string; report_pid: string; report_name: string; incumbent_rel_id: string; confidence: string }>;
  evidence: { note?: string };
  status: string;
  created_at: string;
}

export type DispositionKind = 'net_new' | 'update' | 'conflict' | 'ambiguous' | 'duplicate';
export interface CandidateDisposition {
  requiresDecision?: boolean;
  candidateId: string;
  candidateType: string;
  kind: DispositionKind;
  target?: { kind: 'person' | 'account' | 'edge'; id: string; name: string; via?: string };
  changes?: { field: string; from: string | null; to: string | null }[];
  statements?: string[];
  matches?: { id: string; name: string }[];
  possibleMatch?: { id: string; name: string };
  note?: string;
}

export type CandidateDecisionAction = 'accept' | 'retarget' | 'hold';
export type CandidateDecisionEntityKind = 'person' | 'account' | 'team' | 'territory';
export type CandidateDecisionEndpoint = {
  entity_kind: CandidateDecisionEntityKind;
  id: string;
};
export type CandidateDecisionEndpoints = Record<string, CandidateDecisionEndpoint>;

export interface CandidateDecisionRequest {
  candidate_id: string;
  candidate_version: number;
  action: CandidateDecisionAction;
  resolved_endpoints?: CandidateDecisionEndpoints;
  endpoint_overrides?: CandidateDecisionEndpoints;
  reason?: string;
}

/** The decision route may add server-side audit fields without requiring a frontend release. */
export interface CandidateDecisionResponse {
  candidate_id?: string;
  candidate_version?: number;
  action?: CandidateDecisionAction;
  resolved_endpoints?: CandidateDecisionEndpoints;
  endpoint_overrides?: CandidateDecisionEndpoints;
  status?: string;
  [key: string]: unknown;
}

export interface QueryPerson {
  partner_id: string;
  pid: string;
  name: string;
  email_alias: string | null;
  role: string | null;
  coverage_org: string | null;
  aws_territories?: {code:string;display_name:string}[];
  level: string | null;
  provisional?: boolean;
  vacancy?: boolean;
}

export interface QueryPeopleResponse {
  ok: boolean;
  data: QueryPerson[];
  provenance: ProvenanceRef[];
  warnings: Array<{ kind: string; message: string }>;
}
export interface ReconcileResult {
  changeSetId?: string;
  changeSetSummary?: { text: string; duplicates: number; held: number };
  dispositions: CandidateDisposition[];
  summary: { net_new: number; update: number; conflict: number; ambiguous: number };
}

/** Increment C: a batch member's dispositions, labeled + chip-annotated for the unified batch review. */
export interface BatchDisposition extends CandidateDisposition {
  publication_bucket?: 'ready' | 'uncertain' | 'blocked' | 'left_out';
  label: string;
  confidence: string | null;
  conflicts_with: unknown;
  candidate_version?: number | null;
  candidate_decision?: {
    action: CandidateDecisionAction;
    endpoint_overrides?: CandidateDecisionEndpoints;
    reason?: string;
  } | null;
  resolved_endpoints?: CandidateDecisionEndpoints;
  /** The engine's identity key — duplicate twins share it even when raw labels differ. */
  dup_key: string | null;
}
export interface BatchReconcileMember {
  ingestion_id: string;
  file_label: string;
  status: IngestionStatus;
  candidate_count: number;
  /** Fragment fusion (design §12): non-null when this file was read together with others as one group. */
  fuse_group_id: string | null;
  /** Fusion §6 quarantine: the file's bytes changed after they were read — records can't be trusted. */
  content_integrity_violation: boolean;
  file_mime: string;
  /** True when any record from this file was already approved — such a file can't join a group. */
  has_approved: boolean;
  summary?: ReconcileResult['summary'];
  dispositions?: BatchDisposition[];
}
/** A fragment-fusion group (design §12 "Group UI contract") — files read TOGETHER as one source. */
export interface FuseGroup {
  id: string;
  status: 'detected' | 'extracting' | 'fused' | 'committed' | 'unfused' | 'failed' | 'declined';
  primary_ingestion_id: string | null;
  detect_note: {
    members?: string[];
    overlap?: { key: string; files: string[] }[];
    auto_eligible?: boolean;
    auto_refusion?: boolean;
    absorbed_stragglers?: string[];
    excluded?: { id: string; reason: string }[];
    excluded_truncated?: number;
    conclusion?: string;
    failure?: string;
    reextract_failure?: string;
    /** Machine code for a fuse failure — deterministic ones (missing-integrity-anchor,
     *  too-large-bytes) cannot be fixed by re-minting the group, so the UI hides the retry. */
    failure_code?: string;
    reextract_failure_code?: string;
    calls?: number;
    split_retry?: boolean;
    unsatisfied_joins?: { key: string; files: string[] }[];
    novel_people?: string[];
    /** Member ingestion ids of frames the combined read cited no evidence from (surfaced, not gated). */
    uncited_files?: string[];
    reason?: string;
  } | null;
  fused_model: string | null;
  auto_refusions: number;
  refused_from_group_id: string | null;
  created_at: string;
  updated_at: string;
}
/** Pre-approve assembled-tree instrument (fragment-fusion lane 3): counts + a bounded tree preview over
 *  the batch's pending candidates, so the reviewer sees whether the org chart will assemble or render flat
 *  BEFORE approving. `likely_flat_people` high vs `people` ⇒ the chart collapses (the ISV failure). */
export interface BatchConnectivity {
  /** Every person approval would leave without a place in the org, each with its reason. */
  orgless_people?: Array<{ key: string; name: string; reason: string }>;
  people: number;
  reporting_edges: number;
  resolved_reporting_edges: number;
  roots: number;
  likely_flat_people: number;
  /** Reporting lines naming a DIFFERENT manager than the one on record — approve holds them for review
   *  instead of silently re-parenting (autopsy root cause #3). Zero on a clean-slate ingest. */
  held_manager_claims: number;
  tree_preview: {
    roots: Array<{ key: string; name: string; directs: number; reports_total: number }>;
    people_in_tree: number;
    max_depth: number;
    truncated: boolean;
  };
}
export interface BatchReconcileResult {
  review_classification_ready?: boolean;
  batch: IngestionBatch;
  members: BatchReconcileMember[];
  totals: { net_new: number; update: number; conflict: number; ambiguous: number };
  connectivity: BatchConnectivity;
  /** Director pre-approve surface (Wolf): deterministic findings + planned repairs computed live,
   *  plus the persisted settle-time AI critique (null until that pass has run). */
  director: {
    findings: { code: string; severity: string; detail: string }[];
    repairs: { tabs: number; parents: string[]; teams: string[] };
    // `note` is OPERATIONAL state only ("no model in this context", "critique failed …").
    // `coverage` is what the pass examined when that was less than the whole batch. They used to be
    // one concatenated string and no consumer could tell them apart. `failed` marks a critique that
    // THREW, which a bare `ran:false` does not distinguish from the ordinary no-model case.
    llm: { ran: boolean; looksRight?: boolean; findings?: { code: string; detail: string }[]; note?: string; coverage?: string; scope_bounds?: string; failed?: boolean } | null;
    review_status: 'not_reviewed' | 'queued' | 'running' | 'complete' | 'degraded';
    review_auto_resume?: AutoResumeState | null;
  } | null;
  fuse_groups: FuseGroup[];
  /** Vacated-seat proposals (0048): the batch's historical shape may show seats newer data
   *  flattened away — owner approves/rejects each; nothing auto-applies. */
  /** True when seat detection ERRORED. Distinguishes "no seats found" from "we could not look" —
   *  the panel must say so rather than silently omitting a structural decision. */
  vacancy_detection_failed?: boolean;
  vacancy_proposals: VacancyProposalSummary[] | null;
  /** True when fusion state moved WHILE this response was being computed — the record rows and
   *  the group state describe different moments; refetch before rendering or approving. */
  fusion_changed_midread: boolean;
  /** Seller-book ownership each attributed sheet will assign on publish (B141). */
  ownership?: BatchOwnershipPreview[];
  declared_file_count: number | null;
  realized_count: number;
}
export interface SellerBookSummary {
  ingestion_id: string;
  file_label: string;
  attribution: { status: 'seller' | 'ambiguous' | 'unresolved' | 'none'; seller_pid: string | null;
    seller_name: string | null; reason: string | null; options: string[] } | null;
  colors: number;
  applied: number;
  already_current: number;
  undone: number;
  pending: number;
  listed: Array<{ kind: string; name: string; color: string;
    reason: 'owned_by_other_rep' | 'not_owned' | 'unmatched' | 'ambiguous_target'; lead_name: string | null }>;
}
export interface BatchOwnershipPreview {
  ingestion_id: string;
  file_label: string;
  seller: { pid: string; name: string; basis: Array<'filename' | 'submitter_note' | 'companion_note' | 'chosen'> } | null;
  /** Why no seller could be assigned (conflicting or unverifiable attribution). */
  unresolved: string | null;
  people: number;
  accounts: number;
  already_owned: number;
  other_reps: Array<{ kind: 'person' | 'account'; id: string; name: string; lead_name: string }>;
  /** Explicit colors the book shows; publishing saves them as the seller's ratings. */
  colors?: number;
  colors_on_other_reps?: Array<{ kind: 'person' | 'account'; name: string; color: string; lead_name: string }>;
}
export interface BatchNameFixResult {
  batch_id: string;
  fixes: Array<{ kind: 'person' | 'account'; from: string; to: string }>;
  candidates_updated: number;
  references_updated: number;
}

export interface AutoResumeState {
  attempts: number;
  next_attempt_at: string | null;
  exhausted: boolean;
}

export type WolfRowDecisionChoice = { kind: 'person'; pid: string } | { kind: 'same_account'; account_id: string } | { kind: 'new_account' };
/** One owner decision for one held row: this person, this existing account, or a new account (B426). */
export interface WolfRowDecision {
  run_id: string; candidate_id: string; candidate_type: 'account' | 'account_relationship'; status: string;
  held_reason: string | null; question: string;
  options: Array<{ choice: WolfRowDecisionChoice; label: string }>;
  current_links: Array<{ rel_id: string; pid: string; name: string | null; src_asset: string | null; sourced: boolean }>;
  evidence: { fingerprint: string; parts: Record<string, string> };
}

export interface WolfBatchStatus {
  reask_pending?: boolean;
  batch_id: string;
  status: 'not_started' | 'planning' | 'resolving' | 'awaiting_answers' | 'ready' | 'applying' | 'complete' | 'stale' | 'system_issue';
  run_id?: string;
  created_at?: string;
  total_files?: number;
  total_candidates?: number;
  attempted_candidates?: number;
  item_counts?: Record<string, number>;
  file_counts?: Record<string, number>;
  unresolved_items?: Array<{
    candidate_id: string; candidate_type: string; extracted: Record<string, unknown>;
    status: string; attempts: number; issue_code: string | null;
    outcome: { action?: string; reason?: string } | null;
    proposal: { action?: string; target_id?: string | null; reason?: string } | null;
    created_at: string; updated_at: string; lease_expires_at: string | null;
  }>;
  /** Every open item (pending, held for a question, or a system issue); unresolved_items is a held-first sample of them. */
  unresolved_total?: number;
  system_issues?: Array<{ source: 'candidate' | 'file'; issue_code: string; count: number }>;
  prior_publications?: { applied: number; reconfirmed: number; rejected: number };
  complete_accounting?: boolean;
  coherent?: boolean;
  model_calls?: number;
  call_allowance?: { root_run_id: string; original_limit: number; limit: number; maximum_limit: number;
    changes: Array<{ previous_limit: number; new_limit: number; from_run_id: string; reason: string }> };
  usage?: { input_tokens: string; output_tokens: string; limit_tokens: string | null };
  /** Set only for a stop because the model allowance ran out: whether Try again has room to start. */
  retry_headroom?: { ready: boolean; limited_by: 'calls' | 'tokens' | null; checks_remaining: number;
    checks_used: number; check_limit: number } | null;
  issue_code?: string | null;
  /** An interrupted stop that Starmap resumes by itself; null when a person has to decide. */
  auto_resume?: AutoResumeState | null;
  /** A stop caused by a Starmap problem, which Starmap continues once after each update. */
  update_resume?: { status: 'resumed'; at: string } | { status: 'awaiting_update' | 'awaiting_worker' | 'resuming' }
    | { status: 'tried'; outcome: string } | null;
  /** A stop with model calls that ended with no usage receipt: what continuing would count. */
  unknown_usage?: { status: 'settleable'; calls: number; charge_tokens: string; lineage_calls: number; call_limit: number }
    | { status: 'blocked'; reason: string } | null;
  /** An owner hold: Starmap never resumes this batch by itself while it is set. Only a Superadmin can change it. */
  resume_hold?: ResumeHoldState;
  activation?: { enabled: boolean; reason?: string; run_eligible?: boolean; run_reason?: string };
  questions?: Array<{
    id: string; decision_key: string; question: string;
    competing_facts: Array<{ evidence_id: string; fact: string }>;
    display_question?: string;
    display_options?: string[];
    decision_claims?: import('../../../backend/src/lib/human-question').QuestionClaim[];
    display_sources?: import('../../../backend/src/lib/human-question').QuestionSource[];
    options: string[]; recommendation: string | null; answer: { selected_option: string } | null;
    affected_candidate_ids: string[];
    territory_decision?: {values:{value:string;normalized:string;suggestions:string[];source_values?:string[]}[];registry:{code:string;display_name:string}[]};
    source_decision?: { ingestion_id: string; rows: { question_id: string; candidate_id: string; question: string }[] };
    question_evidence?: { fingerprint: string; parts: Record<string, string> };
    evidence_changes?: string[];
    current_record?: string | null;
    subject_names?: { source_name: string; record_name: string }[];
  }>;
}

export interface ResumeHoldState {
  held: boolean;
  held_by: string | null;
  reason: string | null;
  held_at: string | null;
  history: Array<{ action: 'hold' | 'release'; by: string; reason: string | null; at: string }>;
  can_change: boolean;
}

export interface IngestionCandidate {
  publication_block?: { kind: 'territory' | 'endpoint'; reason: string; action: string } | null;
  id: string;
  /** Optimistic concurrency token supplied by the decision route. Missing versions fail closed in review. */
  candidate_version?: number;
  candidate_type:
    | 'person'
    | 'account'
    | 'relationship'
    | 'account_relationship'
    | 'person_update'
    | 'coverage'
    | 'xref_group_rule'
    | 'person_coverage'
    /** Wolf placement sweep rows: reviewable here but publishable only through Wolf. */
    | 'placement_review'
    | 'person_merge'
    | 'placement_seat';
  extracted: Record<string, unknown>;
  confidence: 'confirmed' | 'high' | 'medium' | 'low' | 'inferred';
  status: 'pending' | 'approved' | 'rejected' | 'superseded';
  conflicts_with: unknown;
  reviewed_by: string | null;
  review_reason: string | null;
  /** Canonical endpoint IDs resolved by the server. Display labels are never promoted into IDs. */
  resolved_endpoints?: CandidateDecisionEndpoints;
  /** Wolf's verdict on this exact row from the batch's latest settled classification-only pass, when there is one. */
  wolf_classification?: WolfClassification | null;
  /** Optional persisted decision projection returned by newer ingestion-detail responses. */
  candidate_decision?: {
    action: CandidateDecisionAction;
    endpoint_overrides?: CandidateDecisionEndpoints;
    reason?: string;
  } | null;
  /** Pass 2 SCOPE knob: 'set_aside' when the concierge's note scope excludes this row (still fully present,
   *  just collapsed in review — "click to include"). Absent/'in_scope' = shown normally. Presentation only. */
  scope_status?: 'in_scope' | 'set_aside';
}

export interface IngestionObservationProjection {
  candidate_id: string;
  projection: string;
}

export interface IngestionObservation {
  id: string;
  source_family: string;
  row_kind: string;
  source_ref: string;
  fragment_index: number | null;
  row_index: number;
  raw_payload: Record<string, unknown>;
  semantic_fingerprint: string;
  evidence: Array<Record<string, unknown>>;
  confidence: IngestionCandidate['confidence'];
  created_at: string;
  projections: IngestionObservationProjection[];
}

export interface SourceVerificationHold {
  id: string;
  event_sequence: string | number;
  partner_id: string;
  ingestion_id: string;
  candidate_id: string | null;
  hold_key: string;
  hold_type:
    | 'field_dispute'
    | 'document_vintage_dispute'
    | 'pixel_verification_unavailable'
    | 'account_identity_ambiguous';
  status: 'open' | 'resolved';
  source_ref: string;
  fragment_index: number | null;
  row_index: number | null;
  field_path: string;
  details: Record<string, unknown>;
  event_fingerprint: string;
  supersedes_event_id: string | null;
  created_at: string;
}

/** Pass 2: the concierge's caution-only steering derived from the upload note (null = none). Display + narration. */
export interface IntakeDirective {
  trustCeiling?: 'confirmed' | 'high' | 'medium' | 'low' | 'inferred';
  recency?: { date?: string; vague?: boolean };
  scope?: { includeRoles?: string[] };
  summary?: string;
}

export interface IngestionReassignment {
  id: string;
  ingestion_id: string;
  from_partner_id: string;
  to_partner_id: string;
  reason: string | null;
  status: 'applied' | 'reverted';
  requested_by: string;
  requested_email: string | null;
  created_at: string;
  applied_at: string | null;
  reverted_by: string | null;
  reverted_email: string | null;
  reverted_at: string | null;
  revert_reason: string | null;
  undo_payload: unknown;
}

export interface IngestionDetail {
  ingestion: {
    review_classification_ready?: boolean;
    undo?: UploadUndoState | null;
    approval_evidence?: { observed_at: string | null } | null;
    id: string;
    partner_id: string;
    uploader_email: string;
    file_mime: string;
    file_bytes: number;
    file_s3_key: string;
    status: IngestionStatus;
    bedrock_model: string | null;
    bedrock_trace_id: string | null;
    extraction_error: string | null;
  failure_records?: UploadFailureRecord[];
    submitter_notes: string | null;
    workspace_signal: Record<string, unknown>;
    workspace_warning: string | null;
    workspace_warning_acknowledged: boolean;
    created_at: string;
    reviewed_at: string | null;
    reviewed_by: string | null;
    review_notes: string | null;
    /** Integrity anchor (fusion §6): sha256 of the bytes extraction actually read. Moves on
     *  every re-extraction/re-anchor — the preview cache keys on it so the reviewer always sees
     *  the bytes approval verifies. */
    extracted_content_sha256: string | null;
    /** The store version pinned with the anchor (D2.1 item 4). */
    file_s3_version_id: string | null;
    /** Uploader-agent batch spine: the drop this file arrived in (null = single-file upload). */
    batch_id: string | null;
    /** Fragment fusion: non-null when this file was read together with others as one group. */
    fuse_group_id: string | null;
    /** The group's current state — 'fused' means approving FINALIZES the fusion (§9 window). */
    fuse_group_status: string | null;
    /** True while this file's drop is still assembling and the file has no group yet —
     *  approving now excludes it from being read together with its batch (design §4). */
    batch_assembling: boolean;
    /** Fusion §6 quarantine: the file's bytes changed after they were read. */
    content_integrity_violation: boolean;
    /** WI-20: the merge's org-tree findings (written at approve time; null = nothing to flag). */
    hierarchy_warning: HierarchyWarning | null;
    hierarchy_warning_acknowledged: boolean;
    /** Pass 2: the concierge's typed steering read from the note (null = none applied). */
    intake_directive: IntakeDirective | null;
    /** A failed upload's detail carries only its metadata and error; leftover records are counted, not sent. */
    failed_detail_compact?: boolean;
    auto_retry_attempts?: number | null;
    auto_retry_next_at?: string | null;
    auto_retry_exhausted?: boolean | null;
    omitted_candidate_count?: number;
    omitted_observation_count?: number;
  };
  candidates: IngestionCandidate[];
  observations?: IngestionObservation[];
  /** Latest-open holds owned by this individual ingestion. */
  verification_holds: SourceVerificationHold[];
  /** Latest-open holds owned by the review anchor. Fused and committed primaries include all current members. */
  review_scope_verification_holds: SourceVerificationHold[];
  reassignments: IngestionReassignment[];
  notifications: IngestionNotification[];
}

export interface HierarchyWarning {
  severity: 'error' | 'warn';
  computed_at: string;
  stats: { persons: number; edges: number; roots: number; maxDepth: number };
  findings: Array<{ code: string; severity: 'error' | 'warn'; detail: string; pids?: string[] }>;
  small_collapse_suspected: boolean;
  forced: boolean;
}

/** v2 candidates carry their receipts in extracted._v2meta. */
export interface CandidateV2Meta {
  evidence: Array<{ spanRef: string; quote: string }>;
  grounded: boolean;
}

export interface IngestionUploadResponse {
  ingestion_id: string;
  partner_id?: string;
  s3_key?: string;
  upload_url?: string;
  expires_in?: number;
  duplicate: boolean;
  resumed?: boolean;
  status?: string;
  /** On a duplicate: the batch the earlier copy arrived in (null = a single-file upload). */
  previous_batch_id?: string | null;
}

export interface IngestionNotification {
  id: string;
  partner_id: string;
  ingestion_id: string;
  event_type: 'upload_registered';
  channel: 'slack';
  status: 'pending' | 'sent' | 'failed' | 'skipped';
  payload: Record<string, unknown>;
  available_at: string;
  sent_at: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  uploader_email?: string | null;
  file_s3_key?: string | null;
  ingestion_status?: IngestionStatus;
  file_mime?: string | null;
  file_bytes?: number | null;
  ingestion_created_at?: string | null;
}

export interface IngestionRollbackEntity {
  id: string;
  name: string;
  created_at: string | null;
  safe_to_delete: boolean;
  blockers: string[];
}

export interface IngestionRollbackPlan {
  ingestion_id: string;
  partner_id: string;
  status?: IngestionStatus;
  candidate_counts: Record<string, number>;
  relationships: { delete: number; retire: number };
  /** v2 retires (not deletes) account links, so `retire` may be set; v1 only ever deletes. */
  account_relationships: { delete: number; retire?: number };
  people: IngestionRollbackEntity[];
  accounts: IngestionRollbackEntity[];
  safe: boolean;
  warnings: string[];
  /** v2 engine only: rows this ingestion superseded that a rollback RESTORED (no v1 equivalent). */
  relationships_restored?: number;
  account_relationships_restored?: number;
  coverage_refreshed?: number;
  coverage_restored?: number;
  coverage_skipped?: number;
  source_rules_retired?: number;
  person_profiles_restored?: number;
  person_coverage_restored?: number;
  person_coverage_skipped?: number;
  /** People, accounts, teams and aliases this upload added that Undo removes. */
  people_removed?: number;
  accounts_removed?: number;
  teams_removed?: number;
  memberships_removed?: number;
  aliases_removed?: number;
  fields_restored?: number;
}

/** A change Undo left as is, and the later change (who, when) it was left for. */
export interface IngestionLaterChange {
  kind: 'upload' | 'correction' | 'other' | 'unrecorded';
  message: string;
  touched: string;
  at: string | null;
  actor: string | null;
  ingestion_id?: string;
  file_name?: string | null;
  correction_id?: string;
  pid?: string;
}

export interface IngestionUndoBoth {
  ingestions: string[];
  corrections: Array<{ pid: string; id: string }>;
}

/** "Undone N of M": the upload's changes reversed (or, in a preview, to be reversed) and those left as is. */
export interface IngestionUndoResult {
  /** Absent only for an Undo recorded before these counts were kept. */
  undone?: number;
  total?: number;
  left_as_is: IngestionLaterChange[];
  /** The later uploads and corrections the left changes were left for, to undo together. */
  undo_both: IngestionUndoBoth | null;
  /** Set when this upload was already undone (a repeat Undo is a no-op). */
  at?: string;
  by?: string | null;
}

export interface IngestionRollbackResponse {
  dry_run: boolean;
  plan: IngestionRollbackPlan;
  already_undone?: boolean;
  undo?: IngestionUndoResult;
  applied?: {
    relationships_deleted: number;
    relationships_retired: number;
    account_relationships_deleted: number;
    /** v2 engine only: account links retired (soft) rather than deleted. */
    account_relationships_retired?: number;
    people_deleted: number;
    accounts_deleted: number;
    /** v2 engine only: restored (un-retired) rows the ingestion had superseded. */
    relationships_restored?: number;
    account_relationships_restored?: number;
    coverage_refreshed?: number;
    coverage_restored?: number;
    coverage_skipped?: number;
    source_rules_retired?: number;
    person_profiles_restored?: number;
  person_coverage_restored?: number;
    person_coverage_skipped?: number;
  };
}

export interface IngestionRollbackCandidate {
  id: string;
  uploader_email: string;
  created_at: string;
  reviewed_at: string | null;
  status: IngestionStatus;
  approved_people: number;
  manager_named_people: number;
  likely_flat_people: number;
}

export interface ExternalImportResultSummary {
  source_records_added: number;
  source_records_updated: number;
  source_records_unchanged: number;
  account_links_inactivated: number;
  activity_records_inactivated: number;
  records_needing_review: number;
  records_failed: number;
}

export interface ExternalRun {
  id: string;
  partner_id: string;
  system: 'salesforce' | 'msa' | 'certinia';
  trigger_type: string;
  status: string;
  rows_seen: number;
  rows_matched: number;
  rows_unmatched: number;
  rows_conflicted: number;
  source_file_name: string | null;
  started_at: string;
  finished_at: string | null;
  triggered_by_email: string | null;
  error_message: string | null;
  metadata?: { result_summary?: ExternalImportResultSummary };
}

export interface ExternalRunDetail {
  run: ExternalRun;
  events: Array<{
    id: string;
    partner_id: string;
    system: string;
    account_id: string | null;
    external_id: string | null;
    run_id: string | null;
    event_type: string;
    actor_email: string | null;
    details: Record<string, unknown>;
    created_at: string;
  }>;
}

export interface ExternalMatch {
  id: string;
  partner_id: string;
  system: 'salesforce' | 'msa' | 'certinia';
  external_id: string | null;
  external_name: string;
  candidate_account_ids: string[];
  suggested_account_id: string | null;
  match_reason: string | null;
  match_confidence: 'high' | 'medium' | 'low';
  status: 'pending' | 'approved' | 'rejected' | 'ignored';
  created_at: string;
}

export interface ExternalActivity {
  id: string;
  partner_id: string;
  account_id: string | null;
  source_system: string;
  external_id: string | null;
  external_name: string;
  activity_type: 'open_opportunity' | 'active_project' | 'pipeline';
  active: boolean;
  activity_count: number | null;
  activity_date: string | null;
  source_name: string | null;
  source_run_id: string | null;
  last_seen_at: string;
  raw_json: Record<string, unknown>;
  matched_account_name?: string | null;
  source_file_name?: string | null;
  source_run_started_at?: string | null;
}

export interface AccountProjectRow {
  project_name: string;
  start_date: string | null;
  end_date: string | null;
  stage: 'active' | 'completed' | 'other';
  status_rag: string | null;
  /** pg numeric arrives as a string over the wire. */
  nps: number | string | null;
  csat: number | string | null;
  source_system: string;
  last_seen_at: string;
}

export interface CoverageHistoryRow {
  event_type: string;
  old_lead_pid: string | null;
  new_lead_pid: string | null;
  old_lead_name: string | null;
  new_lead_name: string | null;
  actor: string | null;
  reason: string | null;
  created_at: string;
}

export interface AccountExternalData {
  account_id: string;
  partner_id: string;
  refs: Array<Record<string, unknown>>;
  salesforce: Array<Record<string, unknown>>;
  salesforce_golden?: Array<Record<string, unknown>>;
  msa: Array<Record<string, unknown>>;
  activity: ExternalActivity[];
  projects?: AccountProjectRow[];
  coverage_history?: CoverageHistoryRow[];
}

export interface SalesforceGoldenReport {
  run_id: string;
  observed_at: string;
  mode: 'dry_run';
  salesforce_writes: number;
  rows_read: number;
  accounts_matched: number;
  accounts_unmatched: number;
  accounts_changed: number;
  snapshots_changed: number;
  stale_rows_held: number;
  territory_excluded: number;
  proposed_fields: number;
  blocked_fields: number;
  territory_filter: string | null;
  changes: { account_id: string; salesforce_account_id: string; field: string; current_value: unknown;
    proposed_value: unknown; person_email: string | null; status: string; reason: string; provenance: unknown[] }[];
  unmatched: { salesforce_account_id: string; name: string; reason: string }[];
}

export interface EvoSyncResult {
  dataset: 'org' | 'book' | 'projects';
  rows_fetched: number;
  /** org/book lanes: staged for admin review, or unchanged since the last pull. */
  status?: 'staged' | 'unchanged';
  ingestion_id?: string;
  existing_status?: string;
  transform_warnings?: number;
  snapshot_run?: Record<string, unknown>;
  run?: Record<string, unknown>;
  /** A zero-row EVO pull (empty 200): nothing imported, existing data preserved. */
  skipped?: 'empty_pull';
  message?: string;
}

export interface AccountExternalSummary {
  account_id: string;
  account_url: string | null;
  open_opportunity_count: number | null;
  latest_closed_won_date: string | null;
  last_project_end: string | null;
  msa_confirmed: boolean;
  msa_proxy: boolean;
  active: boolean;
}

export interface PersonTimelineCoverageEvent {
  scope: 'account' | 'person';
  account_id: string | null;
  target_pid: string | null;
  old_lead_pid: string | null;
  new_lead_pid: string | null;
  old_team_id: string | null;
  new_team_id: string | null;
  event_type: 'assigned' | 'reassigned' | 'removed' | 'derived' | 'rollback';
  actor: string | null;
  reason: string | null;
  created_at: string;
}

export interface PersonTimelineRelationshipEvent {
  src_pid: string;
  dst_pid: string;
  label: string | null;
  rel_type: string | null;
  event_type: 'movement' | 'removed' | 'reconfirmed' | 'superseded';
  reason: string | null;
  created_at: string;
}

export interface PersonTimelineEngagementEvent {
  scope: string;
  key: string;
  status: string | null;
  notes: string | null;
  rated_at: string;
}

export interface PersonTimeline {
  pid: string;
  coverage: PersonTimelineCoverageEvent[];
  relationship: PersonTimelineRelationshipEvent[];
  engagement: PersonTimelineEngagementEvent[];
}

export interface ExternalSettings {
  evo_sync_available?: boolean;
  policy: {
    partner_id: string;
    closed_won_msa_policy: 'treat_as_msa' | 'show_proxy' | 'hide_proxy';
    completed_msa_field_policy: string;
    stale_after_days: number;
    updated_at: string;
  } | null;
  systems: Array<{
    partner_id: string;
    system_type: 'salesforce' | 'msa' | 'certinia';
    status: 'manual_csv' | 'api_ready' | 'disabled';
    config: Record<string, unknown>;
  }>;
}

export interface SuggestionRow {
  id: string;
  scope_type: 'workspace' | 'global' | 'multi';
  partner_id: string | null;
  partner_ids: string[];
  submitter_email: string;
  suggestion_text: string;
  context_url: string | null;
  context_entity_type: string | null;
  context_entity_id: string | null;
  impact: 'nice_to_have' | 'useful' | 'important' | 'blocking' | null;
  category_hint: string | null;
  status: 'new' | 'accepted' | 'deferred' | 'rejected' | 'spec_generated' | 'closed';
  cluster_id: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  canonical_title: string | null;
  category: string | null;
  spec_path: string | null;
  spec_body?: string | null;
  spec_commit_sha: string | null;
  pr_generation_status?: 'not_requested' | 'ready' | 'blocked' | 'created';
  priority_score?: number;
  duplicate_count?: number;
}

export interface SuggestionStats {
  scope: string;
  status: Array<{ status: SuggestionRow['status']; count: number }>;
  categories: Array<{ category: string; count: number; new_count: number; high_impact_count: number }>;
  impacts: Array<{ impact: string; count: number }>;
  scopes: Array<{ scope_type: string; partner_id: string; count: number }>;
  ranked: SuggestionRow[];
}

export type AdminOverviewAreaStatus = 'attention' | 'blocked' | 'clear' | 'unknown';

export interface AdminOverviewArea {
  status: AdminOverviewAreaStatus;
  count: number | null;
  secondary_count?: number | null;
  updated_at: string | null;
}

export interface SellerSubmissionsResponse {
  checked_at: string;
  freshness_rule: { fresh_under_days: number; aging_through_days: number };
  unresolved_uploads: number;
  reps: Array<{
    pid: string;
    name: string;
    freshness: 'never_sent' | 'stale' | 'aging' | 'fresh';
    days_since_submission: number | null;
    last_submission: {
      submitted_at: string;
      file_names: string[];
      path: string;
      publication: 'published' | 'waiting_on_review' | 'processing' | 'problem';
    } | null;
  }>;
}

export type FunctionModelRoutes = Record<'ask' | 'wolf' | 'ingestion', 'native' | 'bedrock'>;

export interface AdminOverviewResponse {
  model_routes?: FunctionModelRoutes;
  checked_at: string;
  areas: {
    review_uploads: AdminOverviewArea;
    failed_uploads: AdminOverviewArea;
    external_matches: AdminOverviewArea;
    duplicate_accounts: AdminOverviewArea;
    integrity_findings: AdminOverviewArea;
    product_ideas: AdminOverviewArea;
    field_intel: AdminOverviewArea;
  };
  /** Superadmin only: when this workspace's native worker last reported in. */
  native_worker?: NativeWorkerLastSeen;
}

export interface NativeWorkerLastSeen {
  /** null when the heartbeat could not be read. */
  fresh: boolean | null;
  last_seen_at: string | null;
  stale_after_seconds: number;
  /** The worker is live but too old to read some queued requests. */
  update_needed?: boolean;
  worker_build_sha?: string | null;
  worker_protocol?: number | null;
  deployed_sha?: string | null;
  api_protocol?: number;
}

export type NativePauseReason = 'worker_offline' | 'worker_update_needed';

export interface NativeWorkerStatus {
  model_routes?: FunctionModelRoutes;
  /** Whether Ask, concierge and uploads run on the native worker in this environment. */
  native: boolean;
  /** Native processing is waiting for a live, up-to-date worker; requests queue until one reports in. */
  paused: boolean;
  reason?: NativePauseReason;
  last_seen_at?: string | null;
  stale_after_seconds?: number;
  worker_build_sha?: string | null;
  worker_protocol?: number | null;
  deployed_sha?: string | null;
  api_protocol?: number;
  held_for_update?: number;
}

export interface SuggestionClusterDetail {
  cluster: Record<string, unknown>;
  suggestions: SuggestionRow[];
  interpretations: Array<Record<string, unknown> & { proposal_json?: FieldIntelProposal | Record<string, never>; proposal_type?: string | null }>;
  events: Array<Record<string, unknown>>;
}

export interface SnapshotSummary {
  quarter: string;
  taken_at: string;
  taken_by: string;
  person_rows: number;
  account_rows: number;
  green: number;
  yellow: number;
  red: number;
  improved: number;
  declined: number;
}

export interface DupeCandidate {
  id: string;
  normalized_key: string;
  account_ids: string[];
  source: string;
  notes: string | null;
  created_at: string;
  accounts: Array<{ account_id: string; name: string; hold: boolean }> | null;
}

export interface IntegrityResponse {
  partner_id?: string;
  ran_at: string;
  clean: boolean;
  checks: Array<{ name: string; status: 'ok' | 'warn' | 'info' | 'fail'; detail: string }>;
}

export interface IntegrityRun {
  id: string;
  partner_id: string;
  trigger_type: string;
  status: 'running' | 'clean' | 'warn' | 'fail' | 'error';
  started_at: string;
  finished_at: string | null;
  summary: Record<string, unknown>;
}

export interface IntegrityEvent {
  id: string;
  partner_id: string;
  finding_id: string | null;
  run_id: string | null;
  event_type: string;
  actor_email: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

export interface IntegrityFinding {
  id: string;
  partner_id: string;
  check_key: string;
  severity: 'info' | 'warn' | 'fail';
  entity_type: string | null;
  entity_id: string | null;
  fingerprint: string;
  title: string;
  detail: string;
  recommendation: string | null;
  status: 'open' | 'acknowledged' | 'resolved' | 'ignored';
  first_seen_at: string;
  last_seen_at: string;
  ignore_reason: string | null;
}

export interface AskCard {
  kind: 'person' | 'account' | 'coverage' | 'metric' | 'suggestion' | 'ingestion';
  title: string;
  subtitle?: string | undefined;
  href?: string | undefined;
  meta?: string[] | undefined;
  vacancy?: { pid?: string; message: string };
}

export interface AskResponse {
  intent:
    | 'coverage'
    | 'town'
    | 'manager_status'
    | 'intro'
    | 'field_intel'
    | 'search'
    | 'answer'
    | 'mutual_accounts'
    | 'ownership_history';
  answer: string;
  cards: AskCard[];
  warnings: string[];
  vacancies?: Array<{ pid?: string; message: string }>;
  suggestion?: { suggestion_id: string; cluster_id: string };
  ingestion?: { ingestion_id: string; candidate_id: string };
  proposal?: FieldIntelProposal;
  /** Where the answer's facts came from (issue #369). The API has returned these since the
   *  `lib/provenance.ts` consolidation; nothing rendered them, so they reached the user as nothing
   *  at all. Mirrors the backend `ProvenanceRef`: EVERY field is optional because provenance is
   *  genuinely partial, and an absent field means "not recorded" — never a claim. */
  citations?: ProvenanceRef[];
}

/** Frontend mirror of the backend `ProvenanceRef` (`backend/src/lib/provenance.ts`). Rows whose
 *  evidence is a non-citation (`EV-legacy`, `legacy`, `unknown`, empty) are already dropped
 *  server-side by `NON_CITATIONS`, so anything arriving here is a real source. */
export interface ProvenanceRef {
  /** WHICH fact this citation supports, in the answer's own terms. Citations MUST be rendered keyed
   *  to this: an unkeyed list under an answer mixing cited and uncited facts invites the reader to
   *  attribute the uncited one to the cited one's document. */
  fact?: string;
  evidence_ref?: string;
  src_asset?: string;
  observed_at?: string;
  since?: string;
  confidence?: string;
  effective_confidence?: string;
}

export interface FieldIntelProposal {
  proposal_type:
    | 'person_update'
    | 'account_update'
    | 'relationship_update'
    | 'account_relationship_update'
    | 'coverage_update'
    | 'duplicate_report';
  raw_user_text: string;
  normalized_change: Record<string, unknown>;
  affected_records: Array<{ kind: 'person' | 'account' | 'relationship' | 'coverage'; id: string; label: string }>;
  before_after_preview: { before: Record<string, unknown>; after: Record<string, unknown> };
  rationale: string;
  confidence: 'low' | 'medium' | 'high' | 'confirmed' | 'inferred';
  effective_date?: string | null;
  requires_review: true;
  parser: 'deterministic' | 'llm';
}

export interface WolfPlacementSweepRow {
  id: string;
  trigger: 'batch' | 'nightly' | 'manual';
  status: 'planning' | 'submitted' | 'nothing_to_review' | 'waiting' | 'failed';
  created_at: string;
  finished_at: string | null;
  people_needing: number;
  accounts_needing: number;
  candidates_created: number;
  batch_id: string | null;
  error: string | null;
  wolf_status: string | null;
}

export interface WolfPlacementSweepStatus {
  sweeps: WolfPlacementSweepRow[];
  reasons: Record<string, number>;
  worker_configured: boolean;
}

export interface TestDataEntity {
  id: string;
  name: string;
  active: boolean;
  alsoCreatedBy: string[];
  liveLinks: string[];
}

/** What removing one upload's test data will change, read before anything is changed. */
export interface TestDataRemovalPlan {
  ingestionId: string;
  file: string;
  createdBy: string;
  createdAt: string;
  status: string;
  batchId: string | null;
  alreadyRolledBack: boolean;
  wolfRuns: { id: string; otherUploads: { id: string; file: string }[] }[];
  wolfRunsAlreadyUndone: string[];
  reviewRollbackNeeded: boolean;
  people: TestDataEntity[];
  accounts: TestDataEntity[];
  /** Teams this upload created, proven by audit history; retired with their upload-only manager. */
  teams?: { id: string; name: string; managerPid: string | null; active: boolean; outsideUses: string[] }[];
}

export interface TestDataRemovalResponse {
  status: 'preview' | 'removed' | 'incomplete';
  plan: TestDataRemovalPlan;
  done?: { wolfRunsUndone: string[]; uploadsRolledBack: string[]; peopleRetired: string[]; teamsRetired?: string[];
    accountsRetired: string[] };
  kept?: { kind: 'person' | 'account' | 'team'; id: string; name: string; why: string }[];
}

export interface DevTestRun {
  id: string;
  partner_id: string;
  label: string;
  created_by: string;
  created_at: string;
  closed_at: string | null;
  upload_count?: number;
  uploads?: { id: string; file_label: string; status: string; batch_id: string | null }[];
  entity_count?: number;
  entities?: { kind: 'person' | 'account' | 'team'; entity_id: string; name: string; active: boolean; creation_audit_id: string }[];
}

export interface DevTestRunRemoval {
  status: 'preview' | 'removed' | 'incomplete' | 'stopped' | 'confirmation_required';
  run: DevTestRun;
  plans?: TestDataRemovalPlan[];
  uploads?: TestDataRemovalResponse[];
  batches?: BatchUndoResponse[];
  kept?: { kind: string; id: string; name: string; why: string }[];
}

/** What undoing one batch will change, read before anything is changed. */
export interface BatchUndoPlan {
  batchId: string;
  createdBy: string | null;
  createdAt: string;
  wolfRuns: string[];
  approvedUploads: string[];
  pendingUploads: string[];
  uploads: { id: string; file: string; status: string; publishedBy: 'wolf' | 'review' | 'none' }[];
  people: { id: string; name: string }[];
  accounts: { id: string; name: string }[];
  wolfChanges: Record<string, number>;
}

export interface BatchUndoLeft { entityType: string; entityId: string; name: string; reason: string }

export interface BatchUndoResponse {
  left?: BatchUndoLeft[];
  status: 'preview' | 'removed';
  plan: BatchUndoPlan;
  done?: { wolfRunsUndone: string[]; uploadsRolledBack: string[]; peopleRetired: string[]; accountsRetired: string[] };
}

export interface UndoJob {
  id: string; kind: 'batch' | 'test_run'; resource_id: string; status: 'queued' | 'running' | 'completed' | 'failed';
  progress: { phase: string; completed: number; total: number }; result: unknown; error: string | null;
}

async function undoCall<T>(path: string, body: unknown, partnerId: string, onProgress?: (job: UndoJob) => void): Promise<T> {
  let response = await call<T | { status: 'queued'; job: UndoJob }>(withPartner(path, partnerId), {
    method: 'POST', body: JSON.stringify(body),
  });
  if (!response || typeof response !== 'object' || !('job' in response)) return response as T;
  let job = response.job;
  for (;;) {
    onProgress?.(job);
    if (job.status === 'completed') return job.result as T;
    if (job.status === 'failed') throw new ApiError(409, 'undo_job_stopped', job.error ?? 'Undo stopped. Review its saved result.', job.result);
    await new Promise(resolve => setTimeout(resolve, 2000));
    job = (await call<{ job: UndoJob }>(withPartner(`/v1/admin/undo-jobs/${encodeURIComponent(job.id)}`, partnerId))).job;
  }
}

// ── Methods ──────────────────────────────────────────────────────────────
function withPartner(path: string, partnerId = 'aws'): string {
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}partner_id=${encodeURIComponent(partnerId)}`;
}

function withAdminScope(path: string, scope?: string): string {
  if (!scope) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}scope=${encodeURIComponent(scope)}`;
}

export interface ConciergeFile {
  filename: string;
  mime: string;
  bytes: number;
}

export interface ConciergeMessage {
  role: 'user' | 'concierge';
  content: string;
  created_at: string;
}

export interface ConciergeThreadResponse {
  ingestion_id: string;
  model_route?: 'subscription_pending' | null;
  note: string | null;
  files: ConciergeFile[];
  thread_id: string | null;
  messages: ConciergeMessage[];
  /**
   * Facts the server establishes about the turn that the model is NOT trusted to repeat — today, that
   * a named seat is VACANT (issue #369). Returned on the POST that produced the turn and deliberately
   * NOT persisted with the message, so it does not replay on a later thread load.
   */
  warnings?: string[];
}

export type VerdictPosture = 'clean' | 'caution' | 'needs_review';

/** Pass 3: the concierge's pre-approve "what do you think" opinion — advisory + display-only, never blocks. */
export interface ConciergeVerdict {
  posture: VerdictPosture;
  opinion: string;
  cautions: { code: string; detail: string }[];
  computedAt?: string;
}

export interface ConciergeVerdictResponse {
  ingestion_id: string;
  /** null = no verdict (not computed / no model / nothing to opine on) — the surface shows nothing. */
  verdict: ConciergeVerdict | null;
}

export interface DevFaultEvidence {
  observed_at: string;
  deployed_sha: string | null;
  fault: { mode: 'stop' | 'duplicate' | 'retry' | 'ack'; state: string; upload_status: string;
    events: { event: string; at: string; detail: Record<string, unknown> }[] };
  calls: { id: string; status: string; usage: Record<string, number> | null }[];
  operations: { id: string; status: string }[];
  duplicate_candidate_keys: string[];
  same_hash_uploads: number;
  cleanup: TestDataRemovalPlan;
}

type DetailPage = { detail_page: { revision: string; offset: number; total_bytes: number; next_offset: number | null; data: string;
  encoding?: 'gzip'; uncompressed_bytes?: number } };

async function readReviewDetail<T>(path: string): Promise<T> {
  const pagedPath = `${path}${path.includes('?') ? '&' : '?'}detail_transport=pages&detail_encoding=gzip`;
  const first = await call<T | DetailPage>(pagedPath);
  if (!first || typeof first !== 'object' || !('detail_page' in first)) return first as T;
  const revision = first.detail_page.revision;
  const total = first.detail_page.total_bytes;
  const encoding = first.detail_page.encoding;
  const expandedSize = first.detail_page.uncompressed_bytes;
  if (encoding !== undefined && encoding !== 'gzip') throw new Error('Unsupported review encoding.');
  if (encoding === 'gzip' && (!Number.isSafeInteger(expandedSize) || expandedSize! < 1 || expandedSize! > 256 * 1024 * 1024))
    throw new Error('Review detail exceeds the supported size.');
  if (!Number.isSafeInteger(total) || total < 1 || total > 256 * 1024 * 1024) throw new Error('Review detail exceeds the supported size.');
  const bytes = new Uint8Array(total);
  let received = 0;
  let page = first.detail_page;
  for (;;) {
    const decoded = Uint8Array.from(atob(page.data), c => c.charCodeAt(0));
    if (page.revision !== revision || page.total_bytes !== total || page.offset !== received
      || page.encoding !== encoding || page.uncompressed_bytes !== expandedSize
      || decoded.length === 0 || received + decoded.length > total) throw new Error('Review changed while loading. Please try again.');
    bytes.set(decoded, received);
    received += decoded.length;
    if (page.next_offset === null) break;
    if (page.next_offset !== received || received >= total) throw new Error('Incomplete review response. Please try again.');
    const next = await call<DetailPage>(`${pagedPath}&detail_offset=${received}&detail_revision=${encodeURIComponent(revision)}`);
    if (!next.detail_page) throw new Error('Review changed while loading. Please try again.');
    page = next.detail_page;
  }
  if (received !== total) throw new Error('Incomplete review response. Please try again.');
  let content = bytes;
  if (encoding === 'gzip') {
    content = new Uint8Array(expandedSize!);
    const reader = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip')).getReader();
    let expanded = 0;
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        if (expanded + value.length > content.length) throw new Error('Review detail exceeds the supported size.');
        content.set(value, expanded);
        expanded += value.length;
      }
      if (expanded !== content.length) throw new Error('Incomplete review response. Please try again.');
    } finally {
      await reader.cancel();
      reader.releaseLock();
    }
  }
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', content));
  const actual = Array.from(digest, b => b.toString(16).padStart(2, '0')).join('');
  if (actual !== revision) throw new Error('Review response could not be verified. Please try again.');
  return JSON.parse(new TextDecoder().decode(content)) as T;
}

export interface ShadowRouteEvent { id: string; route: 'current' | 'new'; reason: string; verdict_run_ids: string[]; actor_email: string; created_at: string }
export interface ShadowRunSummary {
  id: string; files: string[]; status: string; message: string; verdict: 'clean' | 'not_clean' | null; summary: string | null;
  differences: { kind: string; explanation: string }[]; estimate_usd: number | null; remaining_usd: number | null; admitted_usd: number | null;
  skip_reason: string | null; created_at: string; compared_at: string | null;
}
export interface ShadowReport {
  shadow_mode: boolean; durable_pipeline_setting: boolean;
  route: { switch: 'current' | 'new'; effective: 'current' | 'new'; history: ShadowRouteEvent[] };
  streak: { count: number; needed: number; run_ids: string[] }; runs: ShadowRunSummary[];
  notices: { id: string; kind: string; audience: string; body: string; status: string; error: string | null; created_at: string; sent_at: string | null }[];
}

export interface StoredRatingRecovery {
  status: string; applied: boolean; reason?: string; stored_colors?: number;
  ownership?: { seller: { name: string } | null; peopleAssigned: number; accountsAssigned: number; alreadyOwned: number };
  ratings?: { seller: { name: string }; applied: number; alreadyCurrent: number; listed: { name: string; color: string; reason: string; leadName?: string }[] } | null;
  occupied?: { name: string; current: string; stored: string }[];
}

export interface OwnerOrgPlacementBody {
  segment: string; seat_name: string; seat_role: 'DM' | 'LoL' | 'Industry Leader' | 'Director'; report_pids: string[]; statement: string;
}

export const api = {
  placePersonOrg: (pid: string, body: OwnerOrgPlacementBody, partnerId = 'aws') =>
    call<{ correction_id: string; pid: string; confidence: 'inferred' }>(withPartner(`/v1/people/${encodeURIComponent(pid)}/org-placement`, partnerId), { method: 'POST', body: JSON.stringify(body) }),
  restoreStoredRatings: (id: string, body: { reason: string; apply: boolean; confirm_ingestion_id?: string }, partnerId = 'aws') =>
    call<StoredRatingRecovery>(withPartner(`/v1/admin/ingestions/${encodeURIComponent(id)}/stored-ratings`, partnerId), { method: 'POST', body: JSON.stringify(body) }),
  resolveVerificationHolds: (id: string, body: { hold_event_ids: string[]; reason: string; acknowledged_fuse_group_status: string | null }, partnerId = 'aws') =>
    call<{ status: string }>(withPartner(`/v1/ingestions/${encodeURIComponent(id)}/resolve-verification-holds`, partnerId), { method: 'POST', body: JSON.stringify(body) }),
  retryShadowUpload: (id: string, partnerId = 'aws') =>
    call<{ run: ShadowRunSummary }>(withPartner(`/v1/admin/shadow-uploads/${encodeURIComponent(id)}/retry`, partnerId), { method: 'POST' }),
  getPipelineJob: (id: string, partnerId = 'aws') => call<{job: {id:string;status:string;message:string;estimate_usd:string|null;estimate_basis:Record<string,unknown>|null;result?:{questions?:{itemId:string;reason:string}[]}}|null}>(withPartner(`/v1/ingestion-pipeline-jobs/${encodeURIComponent(id)}`,partnerId)),
  pipelineJobAction: (id:string,action:'approve'|'cancel'|'retry',estimate:Record<string,unknown>|null|undefined,partnerId='aws') => call(withPartner(`/v1/ingestion-pipeline-jobs/${encodeURIComponent(id)}/${action}`,partnerId),{method:'POST',body:JSON.stringify({estimate})}),
  devFaultAvailability: (partnerId = 'aws') =>
    call<{ available: boolean }>(withPartner('/v1/admin/dev-faults/availability', partnerId)),
  devFaultEvidence: (id: string, partnerId = 'aws') =>
    call<DevFaultEvidence | null>(withPartner(`/v1/admin/ingestions/${encodeURIComponent(id)}/dev-fault`, partnerId)),
  devFaultAction: (id: string, action: 'stop' | 'duplicate', partnerId = 'aws') =>
    call<{ status: string }>(withPartner(`/v1/admin/ingestions/${encodeURIComponent(id)}/dev-fault`, partnerId), {
      method: 'POST', body: JSON.stringify({ action }),
    }),
  testDataAvailability: (partnerId = 'aws') =>
    call<{ available: boolean }>(withPartner('/v1/admin/test-data/availability', partnerId)),
  listTestRuns: (partnerId = 'aws') =>
    call<{ runs: DevTestRun[] }>(withPartner('/v1/admin/test-data/runs', partnerId)),
  createTestRun: (label: string, partnerId = 'aws') =>
    call<{ run: DevTestRun }>(withPartner('/v1/admin/test-data/runs', partnerId), {
      method: 'POST', body: JSON.stringify({ label }),
    }),
  markTestUploads: (runId: string, ingestionIds: string[], reason: string, partnerId = 'aws') =>
    call<{ run_id: string; marked: string[]; unchanged: string[] }>(
      withPartner(`/v1/admin/test-data/runs/${encodeURIComponent(runId)}/uploads`, partnerId), {
        method: 'POST', body: JSON.stringify({ ingestion_ids: ingestionIds, confirm_run_id: runId, reason }),
      }),
  markTestEntity: (runId: string, body: { kind: 'person' | 'account' | 'team'; entity_id: string; reason: string;
    apply?: boolean; confirm_run_id?: string; confirm_entity_id?: string }, partnerId = 'aws') =>
    call<Record<string, unknown>>(withPartner(`/v1/admin/test-data/runs/${encodeURIComponent(runId)}/entities`, partnerId), {
      method: 'POST', body: JSON.stringify(body),
    }),
  removeTestRun: (runId: string, body: { apply?: boolean; confirm_run_id?: string; reason: string }, partnerId = 'aws', onProgress?: (job: UndoJob) => void) =>
    undoCall<DevTestRunRemoval>(`/v1/admin/test-data/runs/${encodeURIComponent(runId)}/remove`, body, partnerId, onProgress),
  removeTestData: (ingestionId: string, body: { reason: string; apply?: boolean; confirm_ingestion_id?: string },
    partnerId = 'aws') =>
    call<TestDataRemovalResponse>(withPartner(`/v1/admin/ingestions/${encodeURIComponent(ingestionId)}/remove-test-data`, partnerId), {
      method: 'POST', body: JSON.stringify(body),
    }),
  wolfPlacementSweeps: (partnerId = 'aws') =>
    call<WolfPlacementSweepStatus>(withPartner('/v1/admin/wolf-placement-sweep', partnerId)),
  runWolfPlacementSweep: (partnerId = 'aws', scope?: 'district_levels') =>
    call<{ status: 'started' }>(withPartner('/v1/admin/wolf-placement-sweep', partnerId), { method: 'POST', body:JSON.stringify(scope ? {scope} : {}) }),
  latestUndoJob: (kind: 'batch' | 'test_run', resourceId: string, partnerId = 'aws') =>
    call<{ job: UndoJob | null }>(withPartner(`/v1/admin/undo-jobs?kind=${kind}&resource_id=${encodeURIComponent(resourceId)}`, partnerId)),
  undoBatch: (batchId: string, body: { reason: string; apply?: boolean; confirm_batch_id?: string }, partnerId = 'aws', onProgress?: (job: UndoJob) => void) =>
    undoCall<BatchUndoResponse>(`/v1/admin/batches/${encodeURIComponent(batchId)}/undo`, body, partnerId, onProgress),
  engagementSuggestions: (partnerId = 'aws') =>
    call<EngagementRatingSuggestion[]>(withPartner('/v1/engagement/suggestions', partnerId)),
  confirmEngagementSuggestion: (id: string, status: EngagementStatus, partnerId = 'aws') =>
    call<{ id: string; target_pid: string; status: EngagementStatus; set_by_role: 'owner' | 'manager' }>(
      withPartner(`/v1/engagement/suggestions/${encodeURIComponent(id)}/confirm`, partnerId), {
        method: 'POST', body: JSON.stringify({ status }),
      }),
  dismissEngagementSuggestion: (id: string, partnerId = 'aws') =>
    call<{ id: string; dismissed: true }>(withPartner(`/v1/engagement/suggestions/${encodeURIComponent(id)}/dismiss`, partnerId), { method: 'POST' }),
  me: (partnerId = 'aws') => call<MeResponse>(withPartner('/v1/me', partnerId)),
  getSellerSubmissions: (partnerId = 'aws') =>
    call<SellerSubmissionsResponse>(withPartner('/v1/admin/seller-submissions', partnerId)),
  getAdminOverview: (partnerId = 'aws') =>
    call<AdminOverviewResponse>(withPartner('/v1/admin/overview', partnerId)),
  getNativeWorkerStatus: (partnerId = 'aws') =>
    call<NativeWorkerStatus>(withPartner('/v1/native-worker/status', partnerId)),
  /** Re-checks a paused or interrupted request by its saved operation ID; never submits it again. */
  resumeApplicationOperation: <T>(paused: ApplicationPausedDetails, partnerId = 'aws', signal?: AbortSignal) =>
    awaitApplicationOperation<T>(paused.application_operation, paused.expires_at, partnerId, signal, paused.client_id),
  bootstrap: (partnerId = 'aws') =>
    call<BootstrapResponse>(`/v1/bootstrap?partner_id=${encodeURIComponent(partnerId)}`),
  personCorrections: (pid: string, partnerId = 'aws') =>
    call<PersonCorrectionHistory>(withPartner(`/v1/people/${encodeURIComponent(pid)}/corrections`, partnerId)),
  correctPerson: (pid: string, body: PersonProfileCorrection, partnerId = 'aws') =>
    call<{ correction_id: string | null }>(withPartner(`/v1/people/${encodeURIComponent(pid)}`, partnerId), {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  departPerson: (pid: string, body: DepartureBody, partnerId = 'aws') =>
    call<DepartureResult>(withPartner(`/v1/people/${encodeURIComponent(pid)}/departure`, partnerId), {
      method: 'POST', body: JSON.stringify(body),
    }),
  departAccountCounterpart: (accountId: string, body: DepartureBody & {
    role: 'psm' | 'ps_ae'; pid?: string; name?: string; alias?: string;
  }, partnerId = 'aws') =>
    call<DepartureResult>(
      withPartner(`/v1/accounts/${encodeURIComponent(accountId)}/counterparts/departure`, partnerId), {
        method: 'POST', body: JSON.stringify(body),
      }),
  seatAction: <B extends SeatActionBody>(pid: string, body: B, partnerId = 'aws') =>
    call<B extends { action: 'fill' } ? SeatFillResult : B extends { action: 'eliminate' } ? SeatEliminateResult
      : B extends { action: 'reject_fill' } ? SeatRejectFillResult : SeatStateResult>(
      withPartner(`/v1/people/${encodeURIComponent(pid)}/seat`, partnerId), {
        method: 'POST', body: JSON.stringify(body),
      }),
  confirmOrgRoot: (pid: string, partnerId = 'aws') =>
    call<{ correction_id: string | null; pid: string; org_root_confirmed_at: string; org_root_confirmed_by: string }>(
      withPartner(`/v1/people/${encodeURIComponent(pid)}/org-root`, partnerId), {
        method: 'POST', body: JSON.stringify({}),
      }),
  recordSuccession: (predecessorPid: string, body: SuccessionBody, partnerId = 'aws') =>
    call<SuccessionResult>(withPartner(`/v1/people/${encodeURIComponent(predecessorPid)}/succession`, partnerId), {
      method: 'POST', body: JSON.stringify(body),
    }),
  endAccountRelationship: (accountId: string, relId: string, body: { date: string; reason: string }, partnerId = 'aws') =>
    call<{ correction_id: string; pid: string; rel_id: string; ended_on: string }>(
      withPartner(`/v1/accounts/${encodeURIComponent(accountId)}/relationships/${encodeURIComponent(relId)}/end`, partnerId), {
        method: 'POST', body: JSON.stringify(body),
      }),
  undoPersonCorrection: (pid: string, correctionId: string, partnerId = 'aws') =>
    call<{ correction_id: string; undone: true }>(
      withPartner(`/v1/people/${encodeURIComponent(pid)}/corrections/${encodeURIComponent(correctionId)}/undo`, partnerId), {
        method: 'POST',
      }),
  engagementHistory: (scope: 'person' | 'account', key: string, partnerId = 'aws') =>
    call<EngagementHistoryRow[]>(withPartner(`/v1/engagement/history?scope=${scope}&key=${encodeURIComponent(key)}`, partnerId)),
  heatmapImport: (rows: HeatmapImportInput[], partnerId = 'aws', testRunId?: string) =>
    call<HeatmapImportResponseRow[]>(withPartner('/v1/admin/heatmap-import', partnerId), {
      method: 'POST', body: JSON.stringify({ rows, ...(testRunId ? { test_run_id: testRunId } : {}) }),
    }),
  ask: (
    body: {
      question: string;
      mode?: 'auto' | 'answer' | 'field_intel';
      /** Prior turns so a follow-up ("the ISV one") resolves in context. Capped server-side. */
      transcript?: Array<{ role: 'user' | 'assistant'; content: string }>;
    },
    partnerId = 'aws',
    signal?: AbortSignal,
  ) =>
    callApplication<AskResponse>(withPartner('/v1/ask', partnerId), partnerId, {
      method: 'POST',
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    }),
  listDupes: (partnerId = 'aws') => call<{ candidates: DupeCandidate[] }>(withPartner('/v1/dupes', partnerId)),
  listSnapshots: (partnerId = 'aws') => call<{ snapshots: SnapshotSummary[] }>(withPartner('/v1/snapshots', partnerId)),
  getSnapshotRows: (quarter: string, partnerId = 'aws') =>
    call<{ quarter: string; rows: Array<{ scope: 'person' | 'account'; key: string; status: EngagementStatus | null; team_status: EngagementStatus | null }> }>(
      withPartner(`/v1/snapshots/${encodeURIComponent(quarter)}`, partnerId),
    ),
  getIntegrity: (partnerId = 'aws') =>
    call<IntegrityResponse>(withPartner('/v1/integrity', partnerId)),
  runIntegrity: (partnerId = 'aws') =>
    call<{ run_id: string; status: string; open_findings_seen: number; resolved_findings: number }>(
      withPartner('/v1/integrity/run', partnerId),
      { method: 'POST' },
    ),
  listIntegrityRuns: (partnerId = 'aws') =>
    call<{ runs: IntegrityRun[] }>(withPartner('/v1/integrity/runs', partnerId)),
  getLatestIntegrityRun: (partnerId = 'aws') =>
    call<{ run: IntegrityRun | null }>(withPartner('/v1/integrity/latest', partnerId)),
  getIntegrityRun: (id: string, partnerId = 'aws') =>
    call<{ run: IntegrityRun; events: IntegrityEvent[] }>(
      withPartner(`/v1/integrity/runs/${encodeURIComponent(id)}`, partnerId),
    ),
  getIntegrityTrends: (partnerId = 'aws') =>
    call<{
      runs: Array<{ day: string; runs: number; clean: number; warn: number; fail: number; error: number }>;
      findings: Array<{ check_key: string; severity: string; status: string; count: number }>;
    }>(withPartner('/v1/integrity/trends', partnerId)),
  listIntegrityFindings: (status = 'open', partnerId = 'aws') =>
    call<{ findings: IntegrityFinding[] }>(
      withPartner(`/v1/integrity/findings?status=${encodeURIComponent(status)}`, partnerId),
    ),
  getIntegrityFinding: (id: string, partnerId = 'aws') =>
    call<{ finding: IntegrityFinding; events: IntegrityEvent[] }>(
      withPartner(`/v1/integrity/findings/${encodeURIComponent(id)}`, partnerId),
    ),
  patchIntegrityFinding: (
    id: string,
    body: { status: 'open' | 'acknowledged' | 'resolved' | 'ignored'; reason?: string },
    partnerId = 'aws',
  ) =>
    call<{ finding: IntegrityFinding }>(withPartner(`/v1/integrity/findings/${id}`, partnerId), {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  previewSnapshot: (partnerId = 'aws') => call<{ quarter: string; rows: number }>(withPartner('/v1/snapshots/preview', partnerId)),
  createSnapshot: (quarter?: string, partnerId = 'aws', expectedRows?: number) =>
    call<{ quarter: string; rows: number }>(withPartner('/v1/snapshots', partnerId), {
      method: 'POST',
      body: JSON.stringify({ quarter, expected_rows: expectedRows }),
    }),
  dismissDupe: (id: string, notes?: string, partnerId = 'aws') =>
    call<{ id: string; status: string }>(withPartner(`/v1/dupes/${encodeURIComponent(id)}`, partnerId), {
      method: 'PATCH',
      body: JSON.stringify({ action: 'dismiss', notes: notes ?? null }),
    }),
  setEngagementPerson: (
    targetPid: string,
    body: {
      status?: EngagementStatus;
      team_status?: EngagementStatus;
      notes?: string | null;
      rep_pid?: string | null;
      rater_email?: string;
      write_mode?: 'canonical_override';
    },
    partnerId = 'aws',
  ) =>
    call<EngagementWriteResponse>(withPartner(`/v1/engagement/person/${encodeURIComponent(targetPid)}`, partnerId), {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteEngagementPerson: (
    targetPid: string,
    options?: { repPid?: string; writeMode?: 'canonical_override'; field?: 'status' | 'team_status' },
    partnerId = 'aws',
  ) => {
    const qs = new URLSearchParams();
    if (options?.repPid) qs.set('rep', options.repPid);
    if (options?.writeMode) qs.set('write_mode', options.writeMode);
    if (options?.field) qs.set('field', options.field);
    const suffix = qs.size > 0 ? `?${qs.toString()}` : '';
    return call<EngagementWriteResponse & { deleted: true; cleared_field: 'status' | 'team_status' }>(withPartner(`/v1/engagement/person/${encodeURIComponent(targetPid)}${suffix}`, partnerId), {
      method: 'DELETE',
    });
  },
  setEngagementAccount: (
    accountId: string,
    body: {
      status?: EngagementStatus;
      notes?: string | null;
      rep_pid?: string | null;
      rater_email?: string;
      write_mode?: 'canonical_override';
    },
    partnerId = 'aws',
  ) =>
    call<EngagementAccountWriteResponse>(
      withPartner(`/v1/engagement/account/${encodeURIComponent(accountId)}`, partnerId),
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  deleteEngagementAccount: (
    accountId: string,
    options?: { repPid?: string; writeMode?: 'canonical_override' },
    partnerId = 'aws',
  ) => {
    const qs = new URLSearchParams();
    if (options?.repPid) qs.set('rep', options.repPid);
    if (options?.writeMode) qs.set('write_mode', options.writeMode);
    const suffix = qs.size > 0 ? `?${qs.toString()}` : '';
    return call<EngagementAccountWriteResponse & { deleted: true; cleared_field: 'status' }>(withPartner(`/v1/engagement/account/${encodeURIComponent(accountId)}${suffix}`, partnerId), {
      method: 'DELETE',
    });
  },
  patchAccount: (
    accountId: string,
    body: { note?: string | null; hold?: boolean },
    partnerId = 'aws',
  ) =>
    call<Account>(withPartner(`/v1/accounts/${encodeURIComponent(accountId)}`, partnerId), {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  setAccountCoverage: (
    accountId: string,
    body: { caylent_lead_pid: string; caylent_team_id?: string },
    partnerId = 'aws',
  ) =>
    call<{ account_id: string; caylent_team_id: string; caylent_lead_pid: string }>(
      withPartner(`/v1/accounts/${encodeURIComponent(accountId)}/coverage`, partnerId),
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  clearAccountCoverage: (accountId: string, partnerId = 'aws') =>
    call<{ account_id: string; deleted: true }>(
      withPartner(`/v1/accounts/${encodeURIComponent(accountId)}/coverage`, partnerId),
      { method: 'DELETE' },
    ),
  setPersonCoverage: (targetPid: string, caylentLeadPid: string, partnerId = 'aws') =>
    call<{ target_pid: string; caylent_team_id: string; caylent_lead_pid: string }>(
      withPartner(`/v1/people/${encodeURIComponent(targetPid)}/coverage`, partnerId),
      { method: 'PUT', body: JSON.stringify({ caylent_lead_pid: caylentLeadPid }) },
    ),
  removeCoverage: (
    scope: 'person' | 'account', key: string, shownLeadPid: string, storedLeadPid: string | null, partnerId = 'aws',
  ) =>
    call<CoverageRemovalResponse>(
      withPartner(`/v1/${scope === 'person' ? 'people' : 'accounts'}/${encodeURIComponent(key)}/coverage/removals`, partnerId),
      { method: 'POST', body: JSON.stringify({ caylent_lead_pid: shownLeadPid, stored_lead_pid: storedLeadPid }) },
    ),
  undoCoverageRemoval: (scope: 'person' | 'account', key: string, removalId: string, partnerId = 'aws') =>
    call<{ removal_id: string; restored: { caylent_lead_pid: string; caylent_team_id: string } | null }>(
      withPartner(`/v1/${scope === 'person' ? 'people' : 'accounts'}/${encodeURIComponent(key)}/coverage/removals/${encodeURIComponent(removalId)}/undo`, partnerId),
      { method: 'POST' },
    ),
  createIngestion: (body: {
    model_route?: 'native' | 'bedrock';
    test_run_id?: string;
    dev_fault?: 'stop' | 'duplicate' | 'retry' | 'ack';
    filename: string;
    mime: string;
    bytes: number;
    hash: string;
    partner_id?: string;
    workspace_signal?: Record<string, unknown>;
    workspace_warning?: string;
    workspace_warning_acknowledged?: boolean;
    submitter_notes?: string;
    /** WI-2 uploader-declared source type — caps extraction confidence under the v2 engine. */
    source_type?: string;
    /** Uploader-agent batch spine: files dropped together share one client-minted batch_id. */
    batch_id?: string;
    batch_size?: number;
    /** Process this whole multi-file drop again as one new batch, even if its files were ingested before. */
    reprocess?: boolean;
  }) =>
    call<IngestionUploadResponse>('/v1/ingestions', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listIngestions: async (status?: IngestionStatus, partnerId = 'aws', testData?: 'only' | 'all') => {
    const ingestions: IngestionListItem[] = [];
    const seenCursors = new Set<string>();
    let cursor: string | null = null;
    do {
      const query = new URLSearchParams();
      if (status) query.set('status', status);
      if (testData) query.set('test_data', testData);
      if (cursor) query.set('cursor', cursor);
      const page = await call<{ ingestions: IngestionListItem[]; next_cursor?: string | null }>(
        withPartner(`/v1/ingestions${query.size ? `?${query}` : ''}`, partnerId),
      );
      ingestions.push(...page.ingestions);
      cursor = page.next_cursor ?? null;
      if (cursor !== null) {
        if (typeof cursor !== 'string' || !cursor || seenCursors.has(cursor)) {
          throw new ApiError(502, 'invalid_ingestion_pagination', 'We couldn’t load all uploads. Try again.');
        }
        seenCursors.add(cursor);
      }
    } while (cursor);
    return { ingestions };
  },
  getIngestion: (id: string, partnerId = 'aws') =>
    readReviewDetail<IngestionDetail>(withPartner(`/v1/ingestions/${id}`, partnerId)),
  getIngestionFile: (id: string, partnerId = 'aws') =>
    call<{ url: string; mime: string; expires_in: number }>(
      withPartner(`/v1/ingestions/${id}/file`, partnerId),
    ),
  getNativeBatchUsage: (id: string, partnerId = 'aws') =>
    call<{ summaries: Array<{ phase: string; started_calls: number; input_tokens: string; output_tokens: string;
      cache_read_tokens: string; cache_write_tokens: string; unknown_usage_calls: number; pending_calls: number }> }>(
      withPartner(`/v1/ingestion-batches/${encodeURIComponent(id)}/native-usage`, partnerId)),
  getIngestionBatch: (id: string, partnerId = 'aws') =>
    readReviewDetail<IngestionBatchDetail>(withPartner(`/v1/ingestion-batches/${encodeURIComponent(id)}`, partnerId)),
  continueIncompleteBatchReview: (ingestionId: string, partnerId = 'aws') =>
    call<{ status: 'queued'; batch_id: string }>(
      withPartner(`/v1/ingestions/${encodeURIComponent(ingestionId)}/continue-review`, partnerId),
      { method: 'POST' },
    ),
  getIngestionReconcile: (id: string, partnerId = 'aws') =>
    call<ReconcileResult>(withPartner(`/v1/ingestions/${encodeURIComponent(id)}/reconcile`, partnerId)),
  searchPeople: (query: string, partnerId = 'aws') =>
    call<QueryPeopleResponse>(
      withPartner(`/v1/query/people?q=${encodeURIComponent(query)}`, partnerId),
    ),
  decideIngestionCandidate: (
    id: string,
    body: CandidateDecisionRequest,
    partnerId = 'aws',
  ) =>
    call<CandidateDecisionResponse>(
      withPartner(`/v1/ingestions/${encodeURIComponent(id)}/candidate-decision`, partnerId),
      { method: 'POST', body: JSON.stringify(body) },
    ),
  getIngestionBatchReconcile: (id: string, partnerId = 'aws') =>
    call<BatchReconcileResult>(withPartner(`/v1/ingestion-batches/${encodeURIComponent(id)}/reconcile`, partnerId)),
  increaseWolfCallAllowance: (id: string, input: { from_run_id: string; previous_limit: number; new_limit: number; reason: string }, partnerId = 'aws') =>
    call<{ status: string; limit: number }>(withPartner(`/v1/ingestion-batches/${encodeURIComponent(id)}/wolf/call-allowance`, partnerId),
      { method: 'POST', body: JSON.stringify(input) }),
  getWolfBatch: (id: string, partnerId = 'aws') =>
    call<WolfBatchStatus>(withPartner(`/v1/ingestion-batches/${encodeURIComponent(id)}/wolf`, partnerId)),
  /** settleUnknownUsage: the person agreed to count each model call that ended with no usage receipt
   *  at its full reserved size, so the stopped check can continue. */
  startWolfBatch: async (id: string, partnerId = 'aws', options: { settleUnknownUsage?: boolean } = {}) => {
    type Started = { status: 'started'; run_id: string; created: boolean;
      settled_unknown_usage?: { calls: string[]; charged_tokens: string };
      dispatch: 'local_worker_required'; total_candidates: number };
    const accepted = await call<Started | { status: 'queued' | 'preparing'; start_id: string; run_id: string }>(
      withPartner(`/v1/ingestion-batches/${encodeURIComponent(id)}/wolf/start`, partnerId),
      options.settleUnknownUsage ? { method: 'POST', body: JSON.stringify({ settle_unknown_usage: true }) } : { method: 'POST' });
    if (!('start_id' in accepted)) return accepted;
    const deadline = Date.now() + 20 * 60_000;
    for (;;) {
      const state = await call<{ status: string; response: { statusCode: number; body: string } | null }>(
        withPartner(`/v1/ingestion-batches/${encodeURIComponent(id)}/wolf/starts/${encodeURIComponent(accepted.start_id)}`, partnerId));
      if (state.status === 'completed' || state.status === 'refused') {
        if (!state.response) throw new ApiError(502, 'invalid_start_response');
        const result = JSON.parse(state.response.body);
        if (state.response.statusCode >= 400) throw new ApiError(state.response.statusCode, result.error, result.message, { ...result, start_intent_status: state.status, start_id: accepted.start_id });
        return result as Started;
      }
      if (Date.now() >= deadline) throw new ApiError(504, 'wolf_start_pending', 'Checking is still being prepared. Refresh to see its latest status.');
      await new Promise(resolve => setTimeout(resolve, 1500));
    }
  },
  holdWolfResume: (id: string, reason: string, partnerId = 'aws') =>
    call<ResumeHoldState & { changed: boolean }>(
      withPartner(`/v1/ingestion-batches/${encodeURIComponent(id)}/wolf/resume-hold`, partnerId),
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),
  releaseWolfResume: (id: string, partnerId = 'aws') =>
    call<ResumeHoldState & { changed: boolean }>(
      withPartner(`/v1/ingestion-batches/${encodeURIComponent(id)}/wolf/resume-hold/release`, partnerId),
      { method: 'POST' },
    ),
  restartWolfBatch: (id: string, fromRunId: string, partnerId = 'aws') =>
    call<{ status: 'restarted'; run_id: string; from_run_id: string;
      dispatch: 'native_application_worker' | 'local_worker_required'; total_candidates: number }>(
      withPartner(`/v1/ingestion-batches/${encodeURIComponent(id)}/wolf/restart`, partnerId),
      { method: 'POST', body: JSON.stringify({ from_run_id: fromRunId }) },
    ),
  answerWolfQuestion: (runId: string, questionId: string, selectedOption: string, partnerId = 'aws', questionEvidence?: { fingerprint: string; parts: Record<string, string> }, territoryChoices?: Record<string,string>) =>
    call<{ status: 'resumed' | 'held'; resumed_candidate_count: number }>(
      withPartner(`/v1/wolf/runs/${encodeURIComponent(runId)}/questions/${encodeURIComponent(questionId)}/answer`, partnerId),
      { method: 'POST', body: JSON.stringify({ selected_option: selectedOption, question_evidence: questionEvidence, territory_choices: territoryChoices }) },
    ),
  getWolfRowDecision: (runId: string, candidateId: string, partnerId = 'aws') =>
    call<WolfRowDecision>(withPartner(`/v1/wolf/runs/${encodeURIComponent(runId)}/items/${encodeURIComponent(candidateId)}/decision`, partnerId)),
  recordWolfRowDecision: (runId: string, candidateId: string, choice: WolfRowDecisionChoice, questionEvidence: { fingerprint: string; parts: Record<string, string> }, partnerId = 'aws') =>
    call<{ status: 'resumed' | 'held'; resumed_candidate_count: number; selected_option: string }>(
      withPartner(`/v1/wolf/runs/${encodeURIComponent(runId)}/items/${encodeURIComponent(candidateId)}/decision`, partnerId),
      { method: 'POST', body: JSON.stringify({ choice, question_evidence: questionEvidence }) },
    ),
  reaskWolfQuestion: (runId: string, questionId: string, partnerId = 'aws') =>
    call<{ status: 'resumed'; run_id: string }>(withPartner(`/v1/wolf/runs/${encodeURIComponent(runId)}/questions/${encodeURIComponent(questionId)}/answer`, partnerId),
      { method: 'POST', body: JSON.stringify({ reask: true }) }),
  rehearseSlackAmbiguity: (testRunId: string) => call<{checks:string[];[key:string]:unknown}>(
    '/v1/admin/ambiguity-rehearsal', {method:'POST',body:JSON.stringify({test_run_id:testRunId})}),
  decideVacancyProposal: (anchorIngestionId: string, proposalId: string, decision: 'approve' | 'reject', partnerId?: string) =>
    call<{ status: string; reparented: number }>(
      withPartner(`/v1/ingestions/${encodeURIComponent(anchorIngestionId)}/vacancy-proposal`, partnerId),
      { method: 'POST', body: JSON.stringify({ proposal_id: proposalId, decision }) },
    ),
  applyBatchNameFixes: (id: string, partnerId = 'aws') =>
    call<BatchNameFixResult>(withPartner(`/v1/ingestion-batches/${encodeURIComponent(id)}/name-fixes`, partnerId), {
      method: 'POST',
    }),
  /** Fusion lifecycle actions (design §9/§10). unfuse = restore per-file records (review-window
   *  only); re-extract = re-read the group together; join = absorb another file of this batch. */
  unfuseFuseGroup: (batchId: string, groupId: string, partnerId = 'aws') =>
    call<{ fuse_group_id: string; status: string; restored_candidates?: number }>(
      withPartner(`/v1/ingestion-batches/${encodeURIComponent(batchId)}/fuse-groups/${encodeURIComponent(groupId)}/unfuse`, partnerId),
      { method: 'POST' },
    ),
  reextractFuseGroup: (batchId: string, groupId: string, partnerId = 'aws') =>
    call<{ fuse_group_id: string; status: string }>(
      withPartner(`/v1/ingestion-batches/${encodeURIComponent(batchId)}/fuse-groups/${encodeURIComponent(groupId)}/re-extract`, partnerId),
      { method: 'POST' },
    ),
  joinFuseGroup: (batchId: string, groupId: string, ingestionId: string, partnerId = 'aws') =>
    call<{ fuse_group_id: string; status: string }>(
      withPartner(`/v1/ingestion-batches/${encodeURIComponent(batchId)}/fuse-groups/${encodeURIComponent(groupId)}/join`, partnerId),
      { method: 'POST', body: JSON.stringify({ ingestion_id: ingestionId }) },
    ),
  getConciergeThread: (ingestionId: string, partnerId = 'aws') =>
    call<ConciergeThreadResponse>(withPartner(`/v1/concierge/${encodeURIComponent(ingestionId)}`, partnerId)),
  getConciergeVerdict: (ingestionId: string, partnerId = 'aws') =>
    callApplication<ConciergeVerdictResponse>(withPartner(`/v1/concierge/${encodeURIComponent(ingestionId)}/verdict`, partnerId), partnerId),
  sendConciergeMessage: (ingestionId: string, message: string | undefined, partnerId = 'aws') =>
    callApplication<ConciergeThreadResponse>(
      withPartner(`/v1/concierge/${encodeURIComponent(ingestionId)}/messages`, partnerId),
      partnerId,
      { method: 'POST', body: JSON.stringify(message && message.length > 0 ? { message } : {}) },
    ),
  acknowledgeIngestionWarning: (id: string, partnerId = 'aws') =>
    call<{ acknowledged: boolean }>(
      withPartner(`/v1/ingestions/${encodeURIComponent(id)}/acknowledge-warning`, partnerId),
      { method: 'POST' },
    ),
  publicationStatus: (id: string, partnerId = 'aws', publicationId?: string) =>
    call<{ job: PublicationJob | null }>(withPartner(`/v1/ingestions/${id}/publication-status${publicationId ? `?publication_id=${encodeURIComponent(publicationId)}` : ''}`, partnerId)),
  approveIngestion: (
    id: string,
    candidateIds: string[],
    opts?: { notes?: string; force?: boolean; observed_at?: string; acknowledged_fuse_group_status?: string | null; released_set_aside_ids?: string[];
      uncertain_candidate_ids?: string[]; rejected_ids?: string[] },
    partnerId = 'aws',
  ) =>
    (() => {
      const publicationId = crypto.randomUUID();
      return followPublication(() => call<{ job?: PublicationJob }>(withPartner(`/v1/ingestions/${id}/approve`, partnerId), {
      method: 'POST',
      body: JSON.stringify({
        publication_id: publicationId,
        candidate_ids: candidateIds,
        notes: opts?.notes,
        force: opts?.force,
        observed_at: opts?.observed_at,
        acknowledged_fuse_group_status: opts?.acknowledged_fuse_group_status,
        released_set_aside_ids: opts?.released_set_aside_ids,
        uncertain_candidate_ids: opts?.uncertain_candidate_ids,
        rejected_ids: opts?.rejected_ids,
      }),
    }), savedId => api.publicationStatus(id, partnerId, savedId));
    })(),
  getReviewLater: (partnerId = 'aws') => call<{ calls: ReviewLaterCall[] }>(withPartner('/v1/review-later', partnerId)),
  reviewLaterAction: (ingestionId: string, body: { call_id: string; action: 'confirm' | 'undo' | 'switch'; alternative?: number }, partnerId = 'aws') =>
    call<{ status: 'confirmed' | 'switched' | 'undone'; undone?: number; left?: { message: string }[] }>(
      withPartner(`/v1/ingestions/${encodeURIComponent(ingestionId)}/review-later`, partnerId), { method: 'POST', body: JSON.stringify(body) }),
  mergePeople: (
    survivorPid: string,
    duplicatePid: string,
    opts?: { dryRun?: boolean; reason?: string },
    partnerId = 'aws',
  ) =>
    call<{ status: 'merged' | 'preview'; dry_run: boolean; counts: Record<string, number | string[]> }>(
      withPartner('/v1/admin/person-merge', partnerId),
      {
        method: 'POST',
        body: JSON.stringify({
          survivor_pid: survivorPid,
          duplicate_pid: duplicatePid,
          dry_run: opts?.dryRun ?? false,
          reason: opts?.reason,
        }),
      },
    ),
  rejectIngestion: (id: string, reason?: string, partnerId = 'aws') =>
    call<{ ingestion_id: string; status: string }>(withPartner(`/v1/ingestions/${id}/reject`, partnerId), {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  rejectIngestionCandidates: (
    id: string,
    body: { candidate_ids: string[]; reason: string; acknowledged_fuse_group_status: string | null },
    partnerId = 'aws',
  ) =>
    call<{
      ingestion_id: string;
      rejected: string[];
      remaining_pending_candidate_count: number;
      remaining_open_hold_count: number;
      ingestion_status: string;
      fuse_group_id: string | null;
      fuse_group_status: string | null;
    }>(withPartner(`/v1/ingestions/${id}/reject-candidates`, partnerId), {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reassignIngestion: (
    id: string,
    body: { partner_id: string; reason?: string },
    partnerId = 'aws',
  ) =>
    call<{ ingestion_id: string; from_partner_id: string; to_partner_id: string; reassignment: IngestionReassignment }>(
      withPartner(`/v1/ingestions/${id}/reassign`, partnerId),
      { method: 'POST', body: JSON.stringify(body) },
    ),
  revertIngestionReassignment: (
    id: string,
    body: { reassignment_id?: string; reason?: string },
    partnerId = 'aws',
  ) =>
    call<{ ingestion_id: string; from_partner_id: string; to_partner_id: string; reverted_reassignment_id: string }>(
      withPartner(`/v1/ingestions/${id}/revert-reassignment`, partnerId),
      { method: 'POST', body: JSON.stringify(body) },
    ),
  rollbackIngestion: (
    id: string,
    body: { apply?: boolean; reason?: string; also_undo?: IngestionUndoBoth },
    partnerId = 'aws',
  ) =>
    call<IngestionRollbackResponse>(
      withPartner(`/v1/ingestions/${id}/rollback`, partnerId),
      { method: 'POST', body: JSON.stringify(body) },
    ),
  listRollbackCandidates: (partnerId = 'aws') =>
    call<{ candidates: IngestionRollbackCandidate[] }>(
      withPartner('/v1/admin/ingestions/rollback-candidates', partnerId),
    ),
  retryIngestion: (id: string, partnerId = 'aws') =>
    call<{ ingestion_id: string; status?: string; deleted?: boolean; reanchored?: boolean; reason?: string; message?: string }>(
      withPartner(`/v1/ingestions/${id}/retry`, partnerId),
      { method: 'POST' },
    ),
  getShadowUploads: (partnerId = 'aws') => call<ShadowReport>(withPartner('/v1/admin/shadow-uploads', partnerId)),
  switchBackToCurrentPipeline: (reason: string, partnerId = 'aws') =>
    call<{ route: ShadowRouteEvent }>(withPartner('/v1/admin/shadow-uploads/route', partnerId), { method: 'POST', body: JSON.stringify({ route: 'current', reason }) }),
  listUploadNotifications: (status = 'pending', partnerId = 'aws') =>
    call<{ notifications: IngestionNotification[] }>(
      withPartner(`/v1/admin/upload-notifications?status=${encodeURIComponent(status)}`, partnerId),
    ),
  patchUploadNotification: (
    id: string,
    body: { status: 'pending' | 'failed' | 'skipped'; reason?: string; snooze_minutes?: number },
    partnerId = 'aws',
  ) =>
    call<{ notification: IngestionNotification }>(
      withPartner(`/v1/admin/upload-notifications/${encodeURIComponent(id)}`, partnerId),
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  listExternalRuns: (partnerId = 'aws') =>
    call<{ runs: ExternalRun[] }>(withPartner('/v1/admin/external-data/runs', partnerId)),
  getExternalRun: (id: string, partnerId = 'aws') =>
    call<ExternalRunDetail>(withPartner(`/v1/admin/external-data/runs/${encodeURIComponent(id)}`, partnerId)),
  listExternalActivity: (opts?: { status?: 'active' | 'all'; unmatched?: boolean }, partnerId = 'aws') => {
    const qs = new URLSearchParams();
    if (opts?.status === 'all') qs.set('status', 'all');
    if (opts?.unmatched) qs.set('unmatched', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<{ activity: ExternalActivity[] }>(withPartner(`/v1/admin/external-data/activity${suffix}`, partnerId));
  },
  importExternalCsv: (
    system: 'salesforce' | 'msa',
    body: { filename?: string; csv: string },
    partnerId = 'aws',
  ) =>
    call<{
      run_id: string;
      status: string;
      rows_seen: number;
      rows_matched: number;
      rows_unmatched: number;
      rows_conflicted: number;
      result_summary?: ExternalImportResultSummary;
    }>(withPartner(`/v1/admin/external-data/${system}/import`, partnerId), {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listExternalMatches: (status = 'pending', partnerId = 'aws', system?: 'salesforce' | 'msa') => {
    const qs = new URLSearchParams({ status });
    if (system) qs.set('system', system);
    return call<{ matches: ExternalMatch[] }>(
      withPartner(`/v1/admin/external-data/matches?${qs.toString()}`, partnerId),
    );
  },
  patchExternalMatch: (
    id: string,
    body: { status: 'approved' | 'rejected' | 'ignored'; account_id?: string; reason?: string },
    partnerId = 'aws',
  ) =>
    call<{ match_id: string; status: string }>(
      withPartner(`/v1/admin/external-data/matches/${id}`, partnerId),
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  getExternalConflicts: (partnerId = 'aws') =>
    call<{ conflicts: Record<string, unknown> }>(withPartner('/v1/admin/external-data/conflicts', partnerId)),
  getAmbiguityPolicy: (partnerId = 'aws') =>
    call<AmbiguityPolicy>(withPartner('/v1/admin/ambiguity-policy', partnerId)),
  putAmbiguityPolicy: (policy: Pick<AmbiguityPolicy, 'mode' | 'seller_weekly_cap'>, partnerId = 'aws') =>
    call<AmbiguityPolicy>(withPartner('/v1/admin/ambiguity-policy', partnerId), { method: 'PUT', body: JSON.stringify(policy) }),
  getAmbiguityDeliveries: (partnerId = 'aws') =>
    call<AmbiguityDeliveries>(withPartner('/v1/admin/ambiguity-deliveries', partnerId)),
  resendAmbiguityDelivery: (deliveryId: string, partnerId = 'aws') =>
    call<{ status: 'queued' }>(withPartner(`/v1/admin/ambiguity-deliveries/${encodeURIComponent(deliveryId)}/resend`, partnerId), { method: 'POST' }),
  getExternalSettings: (partnerId = 'aws') =>
    call<ExternalSettings>(withPartner('/v1/admin/external-data/settings', partnerId)),
  patchExternalSettings: (
    body: {
      closed_won_msa_policy?: 'treat_as_msa' | 'show_proxy' | 'hide_proxy';
      completed_msa_field_policy?: string;
      stale_after_days?: number;
    },
    partnerId = 'aws',
  ) =>
    call<ExternalSettings>(withPartner('/v1/admin/external-data/settings', partnerId), {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  getAccountExternalData: (accountId: string, partnerId = 'aws') =>
    call<AccountExternalData>(withPartner(`/v1/accounts/${encodeURIComponent(accountId)}/external-data`, partnerId)),
  getPersonTimeline: (pid: string, partnerId = 'aws') =>
    call<PersonTimeline>(withPartner(`/v1/people/${encodeURIComponent(pid)}/timeline`, partnerId)),
  getAccountsExternalSummary: (partnerId = 'aws') =>
    call<{ accounts: AccountExternalSummary[] }>(withPartner('/v1/accounts/external-summary', partnerId)),
  getSalesforceGoldenReport: (partnerId = 'aws') =>
    call<{ report: SalesforceGoldenReport | null; salesforce_writes_enabled: false }>(withPartner('/v1/admin/salesforce/golden-report', partnerId)),
  syncSalesforceGolden: (territory?: string, partnerId = 'aws') =>
    call<SalesforceGoldenReport>(withPartner('/v1/admin/salesforce/golden-sync', partnerId), {
      method: 'POST', body: JSON.stringify(territory ? { territory } : {}),
    }),
  syncEvo: (dataset: 'org' | 'book' | 'projects', partnerId = 'aws') =>
    call<EvoSyncResult>(withPartner('/v1/admin/evo/sync', partnerId), {
      method: 'POST',
      body: JSON.stringify({ dataset }),
    }),
  submitSuggestion: (
    body: {
      suggestion_text: string;
      context_url?: string;
      context_entity_type?: 'person' | 'account' | 'team' | 'tool' | 'admin' | 'global';
      context_entity_id?: string;
      impact?: 'nice_to_have' | 'useful' | 'important' | 'blocking';
      category_hint?: 'ui' | 'data' | 'workflow' | 'ingestion' | 'admin' | 'reporting' | 'performance' | 'other';
      applies_to_all_workspaces?: boolean;
    },
    partnerId = 'aws',
  ) =>
    call<{ suggestion_id: string; cluster_id: string }>(withPartner('/v1/suggestions', partnerId), {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listSuggestions: async (scope = 'workspace', status = 'new', partnerId = 'aws') => {
    const suggestions: SuggestionRow[] = [];
    const seenIds = new Set<string>();
    const seenCursors = new Set<string>();
    let cursor: string | null = null;
    do {
      const query = new URLSearchParams({ scope, status });
      if (cursor) query.set('cursor', cursor);
      const page = await call<{ suggestions: SuggestionRow[]; next_cursor?: string | null }>(
        withPartner(`/v1/admin/suggestions?${query}`, partnerId),
      );
      if (!Array.isArray(page.suggestions)) throw new ApiError(502, 'invalid_suggestion_pagination');
      for (const suggestion of page.suggestions) {
        if (!suggestion || typeof suggestion.id !== 'string' || !suggestion.id) {
          throw new ApiError(502, 'invalid_suggestion_pagination');
        }
        if (seenIds.has(suggestion.id)) continue;
        seenIds.add(suggestion.id);
        suggestions.push(suggestion);
      }
      cursor = page.next_cursor ?? null;
      if (cursor !== null) {
        if (typeof cursor !== 'string' || !cursor || seenCursors.has(cursor)) {
          throw new ApiError(502, 'invalid_suggestion_pagination', 'We couldn’t load all feedback. Try again.');
        }
        seenCursors.add(cursor);
      }
    } while (cursor);
    return { suggestions };
  },
  getSuggestionStats: (scope = 'workspace', partnerId = 'aws') =>
    call<SuggestionStats>(
      withPartner(`/v1/admin/suggestions/stats?scope=${encodeURIComponent(scope)}`, partnerId),
    ),
  generateSuggestionSpec: (id: string, partnerId = 'aws', scope?: string) =>
    call<{ suggestion: SuggestionRow; spec: { path: string; body: string; pr_generation_status: string } }>(
      withPartner(withAdminScope(`/v1/admin/suggestions/${encodeURIComponent(id)}/generate-spec`, scope), partnerId),
      { method: 'POST' },
    ),
  getSuggestionCluster: (id: string, partnerId = 'aws', scope?: string) =>
    call<SuggestionClusterDetail>(
      withPartner(withAdminScope(`/v1/admin/suggestion-clusters/${encodeURIComponent(id)}`, scope), partnerId),
    ),
  patchSuggestionCluster: (
    id: string,
    body: {
      canonical_title?: string;
      category?: string;
      status?: SuggestionRow['status'];
      admin_notes?: string;
      admin_priority_boost?: number;
      scope_type?: 'workspace' | 'global' | 'multi';
      partner_id?: string;
      partner_ids?: string[];
    },
    partnerId = 'aws',
    scope?: string,
  ) =>
    call<{ cluster: Record<string, unknown> }>(
      withPartner(withAdminScope(`/v1/admin/suggestion-clusters/${encodeURIComponent(id)}`, scope), partnerId),
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  mergeSuggestionClusters: (
    id: string,
    body: { source_cluster_ids: string[]; reason?: string },
    partnerId = 'aws',
    scope?: string,
  ) =>
    call<{ moved_suggestions: number; source_cluster_ids: string[] }>(
      withPartner(withAdminScope(`/v1/admin/suggestion-clusters/${encodeURIComponent(id)}/merge`, scope), partnerId),
      { method: 'POST', body: JSON.stringify(body) },
    ),
  splitSuggestionCluster: (
    id: string,
    body: { suggestion_ids: string[]; canonical_title?: string; category?: string; reason?: string },
    partnerId = 'aws',
    scope?: string,
  ) =>
    call<{ cluster_id: string; moved_suggestions: number }>(
      withPartner(withAdminScope(`/v1/admin/suggestion-clusters/${encodeURIComponent(id)}/split`, scope), partnerId),
      { method: 'POST', body: JSON.stringify(body) },
    ),
  patchSuggestion: (
    id: string,
    body: {
      status?: 'new' | 'accepted' | 'deferred' | 'rejected' | 'spec_generated' | 'closed';
      admin_notes?: string;
      scope_type?: 'workspace' | 'global' | 'multi';
      partner_id?: string;
      partner_ids?: string[];
      category?: string;
      canonical_title?: string;
    },
    partnerId = 'aws',
    scope?: string,
  ) =>
    call<{ suggestion: SuggestionRow }>(withPartner(withAdminScope(`/v1/admin/suggestions/${id}`, scope), partnerId), {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};

/**
 * Compute SHA-256 of a File as a lowercase hex string — needed for the
 * ingestion idempotency key.
 */
export async function sha256File(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buf);
  const bytes = Array.from(new Uint8Array(hash));
  return bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface AmbiguityPolicy {
  mode: 'superadmins' | 'account_owner' | 'weekly_digest' | 'channel';
  seller_weekly_cap: number;
  /** Channel mode: where questions are posted, and whether they are only previewed to Superadmins first. */
  channel?: { id: string; name: string; preview: boolean; preview_recipients: string[] };
  superadmins: string[];
}

export interface AmbiguityRecipientDelivery {
  email: string;
  status: 'waiting' | 'sending' | 'failed' | 'uncertain' | 'sent' | 'blocked';
  reason: string;
  deliveryId: string | null;
  attempts: number;
  lastAttemptAt: string | null;
  nextAttemptAt: string | null;
  canResend: boolean;
}

export interface UndeliveredAmbiguity {
  questionId: string;
  questionIds: string[];
  runId: string;
  batchId: string | null;
  question: string;
  blocked: string | null;
  recipients: AmbiguityRecipientDelivery[];
}

export interface AmbiguityDeliveries {
  questions: UndeliveredAmbiguity[];
  checked_at: string;
}
